import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { signOutRequest } from "../../redux/auth/auth.actions";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { toast } from "react-toastify";
import { toastify } from "../../configs/utils";
import { ArrowLeft, Search, X, ChevronDown, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { HiOutlineViewfinderCircle } from "react-icons/hi2";
import { FiTrash } from "react-icons/fi";
import CardInspector from "./CardInspector";
import { useBatchUploadProgress } from "../../context/BatchUploadProgressContext";
import {
  CHUNK_SIZE,
  uploadFilesInChunks,
  finalizeCardUpload,
} from "../../services/cardUpload";

// Import extracted components
import SortableItem from "./SortableItem";
import DashboardControls from "./DashboardControls";
import TableHeaders from "./TableHeaders";
import TableRow from "./TableRow";
import TitleBuilder from "./TitleBuilder";

// Import styled components
import {
  DashboardContainer,
  DashboardTitle,
  DashboardTitleText,
  DashboardCount,
  DashboardStatus,
  DashboardError,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  NoDataCell,
  DashboardPagination,
  PaginationArrow,
  PageButton,
  PageEllipsis,
  ReorderPlaceholder,
  dragDropStyles,
  MagnifiedViewOverlay,
  MagnifiedViewContainer,
  MagnifiedImagesRow,
  DualMagnifiedImage,
} from "./CardsView.styles";
import styled from "styled-components";

// Close button for image modal
const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const ImageLabel = styled.div`
  color: white;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

// Title Builder styled components (matching Upload.jsx design)
const TitleBuilderDropZone = styled.div`
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: visible;
  border: 2px dashed ${(props) => (props.$isDragging ? "#3b82f6" : "#d1d5db")};
  border-radius: 8px;
  padding: 0.75rem;
  background: ${(props) => (props.$isDragging ? "#eff6ff" : "#f9fafb")};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const TitleBuilderFieldItem = styled.div`
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: ${(props) => (props.$isInBuilder ? "default" : "move")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  user-select: none;
  position: relative;
  gap: 0.5rem;
  min-width: 0;
  width: 100%;

  &:hover {
    border-color: #000000;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  &.dragging {
    opacity: 0.5;
  }

  &.in-builder {
    background: white;
    border-color: #000000;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &.drag-over {
    border-top: 3px solid #000000;
    margin-top: 2px;
  }
`;

const TitleBuilderDragHandle = styled.div`
  cursor: move;
  user-select: none;
  display: flex;
  align-items: center;
`;

const TitleBuilderFieldName = styled.input`
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 500;
  min-width: 300px;
  flex-shrink: 0;
  cursor: ${(props) => (props.$isDraggable ? "move" : "pointer")};
  padding: 0.375rem 0.625rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  transition: all 0.2s ease;
  font-family: inherit;
  line-height: 1.4;
  text-align: left;
  pointer-events: auto;
  height: 1.75rem;
  box-sizing: border-box;

  &:hover {
    background: #eff6ff;
  }

  &:focus {
    outline: none;
    background: #eff6ff;
  }
`;

const TitleBuilderAvailableFieldName = styled.input`
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 500;
  width: 100%;
  cursor: move;
  padding: 0.375rem 0.625rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  transition: all 0.2s ease;
  font-family: inherit;
  line-height: 1.4;
  text-align: left;
  pointer-events: auto;
  height: 1.75rem;
  box-sizing: border-box;

  &:hover {
    background: transparent;
  }

  &:focus {
    outline: none;
    background: transparent;
  }
`;

const TitleBuilderFieldInputWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  padding: 0;
  transition: all 0.2s ease;
  position: relative;
  min-width: 0;
  width: 100%;
  max-width: 100%;

  &.field-input-wrapper:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
`;

const TitleBuilderFieldNamePrefix = styled.span`
  padding: 0.375rem 0.625rem;
  font-size: 0.8125rem;
  color: #374151;
  font-weight: 600;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  user-select: none;
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const TitleBuilderFieldInput = styled.textarea`
  flex: 1;
  padding: 0.375rem 0.625rem;
  border: none;
  font-size: 0.8125rem;
  color: #1f2937;
  background: transparent;
  outline: none;
  min-width: 60px;
  min-height: 1.75rem;
  max-height: 150px;
  resize: none;
  overflow-y: auto;
  font-family: inherit;
  line-height: 1.4;

  &[data-input-type="prefix"] {
    border-radius: 6px 0 0 6px;
    border-right: 1px solid #d1d5db;
  }

  &[data-input-type="suffix"] {
    border-radius: 0 6px 6px 0;
    border-left: 1px solid #d1d5db;
  }

  &::placeholder {
    color: #9ca3af;
    font-size: 0.75rem;
  }
`;

const TitleBuilderFieldContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.375rem;
  min-width: 0;
  width: 100%;
`;

const TitleBuilderRemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 0.25rem 0.375rem;
  border-radius: 4px;
  font-size: 1.125rem;
  line-height: 1;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;

  &:hover {
    background: #fee2e2;
    transform: scale(1.1);
  }
`;

const TitleBuilderEmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: #9ca3af;
  font-size: 0.8125rem;
  font-style: italic;
`;

const TitleBuilderAvailableFieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  max-height: 500px;
  overflow-y: auto;
  padding-right: 0.25rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
`;

const TitleBuilderColumnTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.625rem;
  border-bottom: 2px solid #e5e7eb;
  letter-spacing: -0.01em;
`;

const TitleBuilderTwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const TitleBuilderColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const QuickAddFileInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0;
  color: transparent;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;

  &::-webkit-file-upload-button {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    margin-right: 0;
    transition: background-color 0.2s ease;
    font-size: 0.875rem;

    &:hover {
      background: #2563eb;
    }
  }

  &::file-selector-button {
    background: #3b82f6;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    margin-right: 0;
    transition: background-color 0.2s ease;
    font-size: 0.875rem;

    &:hover {
      background: #2563eb;
    }
  }
`;

// Quick Add Image Preview styled components (matching Upload.jsx design)
const QuickAddImagePreviewSection = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
`;

const QuickAddImagePreviewTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const QuickAddImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(460px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QuickAddCardPairContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid #3b82f6;
  border-radius: 12px;
  background: #f9fafb;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const QuickAddImagePreviewItem = styled.div`
  position: relative;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);

    .hover-overlay {
      opacity: 1;
    }
  }
`;

const QuickAddImagePreview = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
`;

const QuickAddImageDetails = styled.div`
  padding: 0.75rem;
  background: white;
  border-top: 1px solid #f3f4f6;
`;

const QuickAddProgressContainer = styled.div`
  margin-top: 0.5rem;
`;

const QuickAddProgressBarBackground = styled.div`
  width: 100%;
  height: 8px;
  background: #f3f4f6;
  border-radius: 999px;
  overflow: hidden;
`;

const QuickAddProgressBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #2563eb, #059669);
  width: ${(props) => props.width || 0}%;
  transition: width 0.25s ease;
`;

const QuickAddImageName = styled.p`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuickAddImageSize = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
`;

const QuickAddHoverOverlay = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

const QuickAddActionButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  color: #374151;
  border: none;
  border-radius: 50%;
  padding: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  svg {
    width: 18px;
    height: 18px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }

  &:hover {
    transform: scale(1.1);
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.view {
    svg {
      color: #2563eb;
      filter: drop-shadow(0 1px 3px rgba(37, 99, 235, 0.3));
    }

    &:hover {
      background: #2563eb;
      svg {
        color: white;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
      }
    }
  }

  &.delete {
    svg {
      color: #dc2626;
      filter: drop-shadow(0 1px 3px rgba(220, 38, 38, 0.3));
    }

    &:hover {
      background: #dc2626;
      svg {
        color: white;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
      }
    }
  }
`;

const QuickAddMagnifiedViewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  cursor: pointer;
`;

const QuickAddMagnifiedViewContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  cursor: default;
`;

const QuickAddMagnifiedImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border: 2px solid white;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const QuickAddCloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  color: #374151;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  font-weight: bold;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover {
    background: white;
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
`;

// Quick Add Preset Modal styled components (matching Upload.jsx design)
const QuickAddModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const QuickAddPresetModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  max-height: 70vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  display: flex;
  flex-direction: column;
`;

const QuickAddPresetModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: white;
`;

const QuickAddPresetModalCloseButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const QuickAddPresetModalSearchSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: white;
`;

const QuickAddPresetModalSearch = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:focus-within {
    background: white;
    border-color: #000000;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const QuickAddPresetModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const QuickAddPresetModalSearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.8125rem;
  color: #1f2937;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }
`;

const QuickAddPresetModalList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
`;

const QuickAddPresetModalItem = styled.div`
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8125rem;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;

  &:hover {
    background: #f9fafb;
    border-color: #000000;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &.selected {
    background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
    border-color: #000000;
    font-weight: 500;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

    &::before {
      content: "✓";
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: #000000;
      font-weight: bold;
      font-size: 1rem;
    }
  }
`;

const QuickAddPresetModalEmptyState = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.8125rem;

  &::before {
    content: "🔍";
    display: block;
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    opacity: 0.5;
  }
`;

const QuickAddPresetModalFooter = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
  flex-shrink: 0;
  background: white;
`;

const QuickAddPresetCount = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: auto;
`;

const QuickAddSearchIconWrapper = styled.div`
  color: #6b7280;
  flex-shrink: 0;
`;

// Batch-specific preset modal styled components
const QuickAddBatchSpecificPresetModalContainer = styled(QuickAddPresetModalContainer)`
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
`;

const QuickAddBatchSpecificPresetModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const QuickAddBatchSpecificPresetModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fafb;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;

    &:hover {
      background: #9ca3af;
    }
  }
`;

const QuickAddBatchSpecificPresetModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const QuickAddPresetsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const QuickAddPresetsOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const QuickAddPresetSelectButton = styled.button`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex: 1;
  background: white;
  color: #374151;
  text-align: left;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex-shrink: 0;
    color: #6b7280;
  }
`;

const QuickAddPresetSelectText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QuickAddBatchSpecificButton = styled.button`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex: 1;
  background: white;
  color: #374151;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuickAddPresetsSubtitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 2rem 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
`;

const QuickAddGrid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const QuickAddSelect = styled.select`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1f2937;
  background: #fafafa2e;
  transition: all 0.2s ease;
  width: 100%;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  outline: none;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15),
      inset 0 -1px 0 rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const QuickAddInput = styled.input`
  padding: 0.875rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1f2937;
  background: #fafafa2e;
  transition: all 0.2s ease;
  width: 100%;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05);

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.1),
      0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15),
      inset 0 -1px 0 rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const QuickAddLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const QuickAddInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const QuickAddButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &.primary {
    background: #3b82f6;
    color: white;

    &:hover:not(:disabled) {
      background: #2563eb;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover:not(:disabled) {
      background: #e5e7eb;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
`;

const QuickAddPreviewBox = styled.div`
  padding: 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #111827;
  line-height: 2;
  white-space: pre-wrap;
  word-wrap: break-word;
  min-height: 40px;

  .preview-field-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border: 1px solid #3b82f6;
    border-radius: 6px;
    color: #1e40af;
    font-weight: 600;
    font-size: 0.875rem;
    margin: 0 0.125rem;
    user-select: none;
  }
`;

const QuickAddSelectedPresetBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #3b82f6;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1e40af;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.15);
  transition: all 0.2s ease;
  width: 100%;
  max-width: 100%;

  &:hover {
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.2);
  }
`;

const QuickAddBadgeRemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  border-radius: 4px;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-left: auto;

  &:hover:not(:disabled) {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Import utility functions
import {
  ensureHttps,
  extractPageNumber,
  normalizePageUrl,
  buildBaseListUrl,
  getColumnDisplayName,
  getOcrStatusColor,
  formatDate,
  initializeImageLoading,
  getFieldMapping,
  cardHasLongTitle,
  getLongTitleBannerMessage,
  LONG_TITLE_MAX_LENGTH,
  STATUS_FILTER_TITLE_LENGTH,
  getPageNumbers,
} from "./CardsView.utils";
import {
  buildSelectAllSyncPayload,
  getEbayBatchSyncPreview,
  hasEbaySyncFilters,
  buildEbaySyncFilters,
  isCardLockedForEdit,
  canEditListedCardField,
  startEbaySync,
} from "../../services/ebaySync";
import { EBAY_SYNC_DETAIL } from "../../routes/routes";
import EbayBatchSyncBar from "../ebay-sync/EbayBatchSyncBar";
import { subscribeBatchOcrProgress } from "../../services/batchSocket";
import BatchOcrProgressPopup from "../common/BatchOcrProgressPopup";

function CardsView({ fixedFilters } = {}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    startChunkedUpload,
    updateChunkProgress,
    startBatchProcessing,
  } = useBatchUploadProgress();

  // Default values for column order and visibility
  const DEFAULT_COLUMN_ORDER = [
    "images",                    // image
    "ebay_title",                // title
    "ebay_card_number",          // card number
    "ebay_card_name",            // card name
    "sale_price",                // sale price
    "available_quantity",        // available quantity
    "status",                    // status
    "ebay_manufacturer",         // manufacturer
    "ebay_set",                  // set
    "ebay_year_manufactured",    // year manufactured
    "ebay_category",             // category
    "ebay_language",             // language
    "ebay_country",              // country of origin
    "ebay_condition",            // condition
    "ebay_condition_description", // condition description
    "ebay_autographed",          // autographed
    "ebay_graded",               // graded
    "ebay_vintage",              // vintage
    "ebay_description",          // description
  ];

  const DEFAULT_COLUMN_VISIBILITY = {
    images: true,
    ebay_title: true,
    ebay_category: true,
    ebay_manufacturer: true,
    ebay_autographed: true,
    ebay_set: true,
    ebay_card_name: true,
    ebay_card_number: true,
    ebay_year_manufactured: true,
    ebay_country: true,
    ebay_vintage: true,
    ebay_language: true,
    ebay_graded: true,
    ebay_condition: true,
    ebay_condition_description: true,
    ebay_description: true,
    sale_price: true,
    available_quantity: true,
    status: true,
  };

  // Helper functions to load from localStorage as fallback (API is checked first in useEffect)
  const loadColumnOrder = () => {
    try {
      const saved = localStorage.getItem("dashboard_columnOrder");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that all default columns are present
        const allColumns = [...new Set([...DEFAULT_COLUMN_ORDER, ...parsed])];
        return allColumns.filter((col) => DEFAULT_COLUMN_ORDER.includes(col));
      }
    } catch (error) {
      console.error("Error loading column order from localStorage:", error);
    }
    return DEFAULT_COLUMN_ORDER;
  };

  const loadColumnVisibility = () => {
    try {
      const saved = localStorage.getItem("dashboard_columnVisibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to ensure all columns are present
        return { ...DEFAULT_COLUMN_VISIBILITY, ...parsed };
      }
    } catch (error) {
      console.error(
        "Error loading column visibility from localStorage:",
        error
      );
    }
    return DEFAULT_COLUMN_VISIBILITY;
  };

  // State management
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [count, setCount] = useState(0);
  const [hasLongTitles, setHasLongTitles] = useState(false);
  const [longTitleCount, setLongTitleCount] = useState(0);
  const [actualReadyCardsCount, setActualReadyCardsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [ordering, setOrdering] = useState("uploaded_at");
  const [search, setSearch] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [batchViewMode, setBatchViewMode] = useState("overview");
  const [inspectorSelectedCard, setInspectorSelectedCard] = useState(null);
  const [imageLoading, setImageLoading] = useState({});
  const [imageLoaded, setImageLoaded] = useState({});
  const [refreshingCardIds, setRefreshingCardIds] = useState(new Set());
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
  const [activeId, setActiveId] = useState(null);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isAllPagesSelected, setIsAllPagesSelected] = useState(false);
  const [allSelectedCardIds, setAllSelectedCardIds] = useState(new Set());
  const [deselectedCardIds, setDeselectedCardIds] = useState(new Set());
  const [columnFilters, setColumnFilters] = useState({
    status: "",
    ocr_status: "",
  });
  const [cardsPerPage, setCardsPerPage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return parseInt(urlParams.get("page_size")) || 100;
  });
  const [columnVisibility, setColumnVisibility] =
    useState(DEFAULT_COLUMN_VISIBILITY);

  // Edit mode state
  const [isEditModeEnabled, setIsEditModeEnabled] = useState(true);
  const prevBatchIdRef = useRef(undefined);
  const isLoadingTablePersonalizeRef = useRef(false);
  const currentListUrlRef = useRef(null);

  // Modal states
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isPersonalizeTableModalOpen, setIsPersonalizeTableModalOpen] =
    useState(false);
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
  const [isListOnEbayModalOpen, setIsListOnEbayModalOpen] = useState(false);
  const [isListingOnEbay, setIsListingOnEbay] = useState(false);
  const [batchSyncPreview, setBatchSyncPreview] = useState(null);
  const [batchListableCount, setBatchListableCount] = useState(null);
  const [activePersonaliseTab, setActivePersonaliseTab] = useState("showHide");
  const requiredPolicies = useSelector((state) => state.ebay.requiredPolicies);
  const isEbayConnected = useSelector((state) => state.ebay.connected);

  // Bulk edit data
  const [bulkEditData, setBulkEditData] = useState({
    columnName: "",
    newValue: "",
  });

  // Quick add form data
  const [quickAddData, setQuickAddData] = useState({
    batchName: "",
    titleBuilder: [], // Changed to array for drag-and-drop Title Builder
    selectedFiles: [],
    uploadProgress: {},
    isUploading: false,
    error: "",
    uploadStatus: "",
    isPresetsOpen: false,
    defaultMetadata: null,
    presetsDraft: {
      Category: "",
      SuggestedPrice: "",
      Condition: "",
      Metadata: {
        "Card Game / Type": "",
        Set: "",
        SubSet: "",
        Variant: "",
        Year: "",
        Rarity: "",
        Edition: "",
        Autograph: "",
        Language: "",
        Graded: "",
        Grade: "",
        Condition: "",
        Producer: "",
        "Special Features": "",
        Damage: "",
        "Selling Points": "",
        "Market Value": "",
      },
      eBayListing: {
        Category: "",
        Condition: "",
        ItemSpecifics: {
          Brand: "",
          "Card Name": "",
          Set: "",
          SubSet: "",
          Variant: "",
          Year: "",
          Rarity: "",
          Condition: "",
          "Country/Region of Manufacture": "",
        },
      },
    },
  });

  // Title Builder modal state
  const [isTitleBuilderOpen, setIsTitleBuilderOpen] = useState(false);
  const [draggedField, setDraggedField] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [isDraggingOverDropZone, setIsDraggingOverDropZone] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const titleBuilderDropZoneRef = useRef(null);
  const [availableFields] = useState([
    "Card Name",
    "Type",
    "Manufacturer",
    "Set",
    "Card Number",
    "Year Manufactured",
  ]);

  // Selected card and image states
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCardForModal, setSelectedCardForModal] = useState(null);
  const [quickAddMagnifiedImage, setQuickAddMagnifiedImage] = useState(null);
  const quickAddActionsRef = useRef(null);
  const prevQuickAddFilesCountRef = useRef(0);
  const [quickAddPresetsList, setQuickAddPresetsList] = useState([]);
  const [quickAddSelectedPreset, setQuickAddSelectedPreset] = useState(null);
  const [isQuickAddPresetModalOpen, setIsQuickAddPresetModalOpen] =
    useState(false);
  const [quickAddPresetSearchQuery, setQuickAddPresetSearchQuery] =
    useState("");
  const [isQuickAddBatchSpecificPresetModalOpen, setIsQuickAddBatchSpecificPresetModalOpen] =
    useState(false);
  const [quickAddBatchSpecificPresetName, setQuickAddBatchSpecificPresetName] = useState("");
  const [quickAddBatchSpecificPresetFormData, setQuickAddBatchSpecificPresetFormData] =
    useState({
      presetName: "",
      category: "",
      sportGameFranchise: "",
      manufacturer: "",
      autographed: "",
      graded: "",
      condition: "",
      language: "",
      set: "",
      yearManufactured: "",
      vintage: "",
      conditionDescription: "",
      country: "",
    });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get batch name from fixedFilters or URL
  const batchName = fixedFilters?.["batch__name"] || "All Cards";

  const ebaySyncFilterInput = { search, columnFilters };
  const activeEbaySyncFilters = buildEbaySyncFilters(ebaySyncFilterInput);
  const hasActiveCardFilters = hasEbaySyncFilters(activeEbaySyncFilters);

  // State for batch title builder configuration
  const [batchTitleBuilderConfig, setBatchTitleBuilderConfig] = useState(null);
  const [isLoadingTitleBuilder, setIsLoadingTitleBuilder] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [batchOcrProgress, setBatchOcrProgress] = useState(null);

  // Title Builder Modal state
  const [isTitleBuilderModalOpen, setIsTitleBuilderModalOpen] = useState(false);
  const [titleBuilderType, setTitleBuilderType] = useState("global"); // "global" or "batch"
  const [titleBuilderConfig, setTitleBuilderConfig] = useState([]);
  const [titleBuilderDraggedField, setTitleBuilderDraggedField] =
    useState(null);
  const [titleBuilderDraggedIndex, setTitleBuilderDraggedIndex] =
    useState(null);
  const [titleBuilderDragOverIndex, setTitleBuilderDragOverIndex] =
    useState(null);
  const [
    titleBuilderIsDraggingOverDropZone,
    setTitleBuilderIsDraggingOverDropZone,
  ] = useState(false);
  const [titleBuilderEditingFieldIndex, setTitleBuilderEditingFieldIndex] =
    useState(null);
  const [titleBuilderIsSaving, setTitleBuilderIsSaving] = useState(false);
  const [titleBuilderIsLoading, setTitleBuilderIsLoading] = useState(false);
  const [titleBuilderAvailableFields] = useState([
    "Card Name",
    "Type",
    "Manufacturer",
    "Set",
    "Card Number",
    "Year Manufactured",
  ]);

  const DEFAULT_BASE_URL = "http://localhost:8000";
  const API_BASE_URL = ensureHttps(
    import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL
  );

  // API functions
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  // Fetch ready cards count
  const fetchReadyCardsCount = useCallback(async () => {
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // Build URL with status=ready filter and page_size=1 to just get the count
      const urlParams = new URLSearchParams();
      urlParams.set("status", "ready");
      urlParams.set("page_size", "1");
      if (fixedFilters?.["batch__name"]) {
        urlParams.set("batch__name", fixedFilters["batch__name"]);
      }
      if (search && search.trim().length > 0) {
        urlParams.set("search", search.trim());
      }

      const requestUrl = `${API_BASE_URL}/api/v1/cards/?${urlParams.toString()}`;

      let response = await fetch(requestUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(requestUrl, {
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
        });
      }

      if (response.ok) {
        const data = await response.json();
        setActualReadyCardsCount(data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching ready cards count:", error);
      // Don't set error state, just log it
    }
  }, [API_BASE_URL, fixedFilters, search]);

  // Fetch listable (eBay sync preview) count for the current batch
  const fetchBatchListableCount = useCallback(async () => {
    if (!currentBatchId) {
      setBatchListableCount(null);
      return;
    }

    try {
      const preview = await getEbayBatchSyncPreview(
        currentBatchId,
        ebaySyncFilterInput,
        API_BASE_URL
      );
      setBatchListableCount(preview?.to_process ?? null);
    } catch {
      setBatchListableCount(null);
    }
  }, [currentBatchId, API_BASE_URL, search, columnFilters]);

  // Silent refresh of current list page + ready count (after title edits)
  const refreshListAfterTitleEdit = useCallback(async () => {
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const requestUrl =
        currentListUrlRef.current ??
        buildBaseListUrl(API_BASE_URL, {
          ordering,
          search,
          fixedFilters,
          columnFilters,
          batchViewMode,
          cardsPerPage,
        });

      let response = await fetch(requestUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(requestUrl, {
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
        });
      }

      if (!response.ok) return;

      const data = await response.json();
      const cardsData = Array.isArray(data.results) ? data.results : [];
      setCards(cardsData);
      setHasLongTitles(data.has_long_titles === true);
      setLongTitleCount(
        typeof data.long_title_count === "number" ? data.long_title_count : 0
      );

      if (inspectorSelectedCard) {
        const refreshed = cardsData.find(
          (c) => c.id === inspectorSelectedCard.id
        );
        if (refreshed) {
          setInspectorSelectedCard((prev) => ({ ...prev, ...refreshed }));
        }
      }

      await fetchReadyCardsCount();
      await fetchBatchListableCount();
    } catch (error) {
      console.error("Error refreshing list after title edit:", error);
    }
  }, [
    API_BASE_URL,
    cardsPerPage,
    ordering,
    search,
    columnFilters,
    batchViewMode,
    fixedFilters,
    fetchReadyCardsCount,
    fetchBatchListableCount,
    inspectorSelectedCard,
  ]);

  // Fetch batch title builder configuration
  const fetchBatchTitleBuilder = async (batchId) => {
    if (!batchId) return;

    setIsLoadingTitleBuilder(true);
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      let response = await fetch(
        `${API_BASE_URL}/api/v1/cards/batches/${batchId}/title-builder/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(
          `${API_BASE_URL}/api/v1/cards/batches/${batchId}/title-builder/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response.ok) {
        const data = await response.json();
        setBatchTitleBuilderConfig(data);

        // Console log: Title Builder Config Keys
        if (data.configuration && Array.isArray(data.configuration)) {
          const configKeys = data.configuration
            .filter((item) => item.type === "field-name")
            .map((item) => item.value);
          console.log("=== TITLE BUILDER CONFIG KEYS ===", configKeys);
        }
      } else if (response.status === 404) {
        // No configuration found, set empty
        setBatchTitleBuilderConfig({
          configuration: [],
          is_same_as_global: false,
        });
        console.log("=== TITLE BUILDER CONFIG KEYS ===", []);
      } else {
        console.error(
          "Failed to fetch batch title builder:",
          response.statusText
        );
        setBatchTitleBuilderConfig(null);
      }
    } catch (err) {
      console.error("Error fetching batch title builder:", err);
      setBatchTitleBuilderConfig(null);
    } finally {
      setIsLoadingTitleBuilder(false);
    }
  };

  // Fetch batch table personalization configuration
  const fetchBatchTablePersonalize = async (batchId) => {
    if (!batchId) return null;

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      let response = await fetch(
        `${API_BASE_URL}/api/v1/cards/batches/${batchId}/table-personalise/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(
          `${API_BASE_URL}/api/v1/cards/batches/${batchId}/table-personalise/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response.ok) {
        const data = await response.json();
        if (
          data.table_personalise &&
          Array.isArray(data.table_personalise) &&
          data.table_personalise.length === 2
        ) {
          const columnOrder = data.table_personalise[0];
          const columnVisibility = data.table_personalise[1];

          // Check if columnOrder is empty or invalid - use defaults
          if (
            !columnOrder ||
            !Array.isArray(columnOrder) ||
            columnOrder.length === 0
          ) {
            return null; // Use defaults
          }

          // Check if columnVisibility is empty or invalid - use defaults
          if (
            !columnVisibility ||
            typeof columnVisibility !== "object" ||
            Object.keys(columnVisibility).length === 0
          ) {
            return null; // Use defaults
          }

          return {
            columnOrder: columnOrder,
            columnVisibility: columnVisibility,
          };
        }
        // If structure is invalid, return null to use defaults
        return null;
      } else if (response.status === 404) {
        // No configuration found, return null to use defaults
        return null;
      } else {
        console.error(
          "Failed to fetch batch table personalization:",
          response.statusText
        );
        return null;
      }
    } catch (err) {
      console.error("Error fetching batch table personalization:", err);
      return null;
    }
  };

  // Save batch table personalization configuration
  const saveBatchTablePersonalize = async (
    batchId,
    columnOrder,
    columnVisibility
  ) => {
    if (!batchId) return false;

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const payload = {
        table_personalise: [columnOrder, columnVisibility],
      };

      let response = await fetch(
        `${API_BASE_URL}/api/v1/cards/batches/${batchId}/table-personalise/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(
          `${API_BASE_URL}/api/v1/cards/batches/${batchId}/table-personalise/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );
      }

      if (response.ok) {
        return true;
      } else {
        console.error(
          "Failed to save batch table personalization:",
          response.statusText
        );
        return false;
      }
    } catch (err) {
      console.error("Error saving batch table personalization:", err);
      return false;
    }
  };

  const fetchCards = useCallback(
    async (url = null, retry = false) => {
      try {
        setIsLoading(true);

        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        const requestUrl =
          url ??
          buildBaseListUrl(API_BASE_URL, {
            ordering,
            search,
            fixedFilters,
            columnFilters,
            batchViewMode,
            cardsPerPage,
          });
        currentListUrlRef.current = requestUrl;

        let response = await fetch(requestUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401 && !retry) {
          const newToken = await refreshAccessToken();
          response = await fetch(requestUrl, {
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          });
        }

        if (!response.ok)
          throw new Error(`Failed to fetch cards: ${response.status}`);

        const data = await response.json();
        const cardsData = Array.isArray(data.results) ? data.results : [];
        setCards(cardsData);
        setNextPage(normalizePageUrl(data.next, API_BASE_URL));
        setPrevPage(normalizePageUrl(data.previous, API_BASE_URL));
        setCount(data.count);
        setHasLongTitles(data.has_long_titles === true);
        setLongTitleCount(
          typeof data.long_title_count === "number" ? data.long_title_count : 0
        );
        
        // Fetch ready cards count
        fetchReadyCardsCount();
        fetchBatchListableCount();

        // Console log: API Card Metadata Keys and Values (for title building)
        if (cardsData.length > 0) {
          const firstCard = cardsData[0];
          const apiKeys = {
            "Card Name":
              firstCard?.metadata?.detailed_metadata?.["Card Name"] !==
              undefined
                ? "detailed_metadata['Card Name']"
                : null,
            Type:
              firstCard?.metadata?.detailed_metadata?.["Card Game / Type"] !==
              undefined
                ? "detailed_metadata['Card Game / Type']"
                : null,
            Manufacturer:
              firstCard?.metadata?.detailed_metadata?.["Producer"] !== undefined
                ? "detailed_metadata['Producer']"
                : null,
            Set:
              firstCard?.metadata?.detailed_metadata?.["Set"] !== undefined
                ? "detailed_metadata['Set']"
                : null,
            "Card Number":
              firstCard?.metadata?.detailed_metadata?.["Card Number"] !==
              undefined
                ? "detailed_metadata['Card Number']"
                : null,
            "Year Manufactured":
              firstCard?.metadata?.detailed_metadata?.["Year"] !== undefined
                ? "detailed_metadata['Year']"
                : null,
          };
          const availableApiKeys = Object.entries(apiKeys)
            .filter(([_, path]) => path !== null)
            .map(([key, _]) => key);
          console.log(
            "=== API CARD METADATA KEYS (for title building) ===",
            availableApiKeys
          );

          // Log actual API values
          const apiValues = {
            "Card Name":
              firstCard?.metadata?.detailed_metadata?.["Card Name"] || null,
            Type:
              firstCard?.metadata?.detailed_metadata?.["Card Game / Type"] ||
              null,
            Manufacturer:
              firstCard?.metadata?.detailed_metadata?.["Producer"] || null,
            Set: firstCard?.metadata?.detailed_metadata?.["Set"] || null,
            "Card Number":
              firstCard?.metadata?.detailed_metadata?.["Card Number"] || null,
            "Year Manufactured":
              firstCard?.metadata?.detailed_metadata?.["Year"] || null,
          };
          console.log(
            "=== API CARD METADATA VALUES (for title building) ===",
            apiValues
          );
        }

        // Extract batch ID from first card if available and fetch title builder
        if (cardsData.length > 0 && cardsData[0].batch) {
          const batchId =
            typeof cardsData[0].batch === "object"
              ? cardsData[0].batch.id
              : cardsData[0].batch;
          if (batchId) {
            // Reset to defaults first when loading a batch (or switching batches)
            // API will be checked first, and only if API values are empty, defaults will be used
            const batchChanged = prevBatchIdRef.current !== undefined && prevBatchIdRef.current !== batchId;
            if (batchChanged || prevBatchIdRef.current === undefined) {
              // Reset to defaults when switching batches or on initial load
              setColumnOrder(DEFAULT_COLUMN_ORDER);
              setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
            }

            setCurrentBatchId(batchId);
            fetchBatchTitleBuilder(batchId);
            // Fetch table personalization for this batch - API is checked FIRST
            isLoadingTablePersonalizeRef.current = true;
            fetchBatchTablePersonalize(batchId).then((config) => {
              isLoadingTablePersonalizeRef.current = false;
              if (config) {
                // API returned valid data - use it
                // Validate column order: preserve API order, add missing columns from defaults
                const apiOrder = config.columnOrder.filter((col) => DEFAULT_COLUMN_ORDER.includes(col));
                const missingColumns = DEFAULT_COLUMN_ORDER.filter((col) => !apiOrder.includes(col));
                const validOrder = [...apiOrder, ...missingColumns];
                // Merge column visibility with defaults
                const validVisibility = { ...DEFAULT_COLUMN_VISIBILITY, ...config.columnVisibility };
                setColumnOrder(validOrder);
                setColumnVisibility(validVisibility);
                // Also save to localStorage as fallback
                try {
                  localStorage.setItem("dashboard_columnOrder", JSON.stringify(validOrder));
                  localStorage.setItem("dashboard_columnVisibility", JSON.stringify(validVisibility));
                } catch (error) {
                  console.error("Error saving to localStorage:", error);
                }
              } else {
                // API returned empty/null values - use defaults (already set above)
                // Clear localStorage for this batch to ensure defaults are used
                try {
                  localStorage.removeItem("dashboard_columnOrder");
                  localStorage.removeItem("dashboard_columnVisibility");
                } catch (error) {
                  console.error("Error clearing localStorage:", error);
                }
              }
            }).catch(() => {
              isLoadingTablePersonalizeRef.current = false;
              // On API error, try localStorage as fallback, otherwise use defaults
              const fallbackOrder = loadColumnOrder();
              const fallbackVisibility = loadColumnVisibility();
              setColumnOrder(fallbackOrder);
              setColumnVisibility(fallbackVisibility);
            });
          } else {
            isLoadingTablePersonalizeRef.current = false;
          }
        } else {
          setCurrentBatchId(null);
          // Reset to defaults when no batch
          setColumnOrder(DEFAULT_COLUMN_ORDER);
          setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
        }

        // Initialize image loading state for all cards
        const { loadingState, loadedState } = initializeImageLoading(cardsData);
        setImageLoading(loadingState);
        setImageLoaded(loadedState);

        // Determine current page
        const pageFromNext = extractPageNumber(
          normalizePageUrl(data.next, API_BASE_URL)
        );
        const pageFromPrev = extractPageNumber(
          normalizePageUrl(data.previous, API_BASE_URL)
        );

        if (!pageFromNext && !pageFromPrev) {
          setCurrentPage(1);
        } else if (pageFromPrev) {
          setCurrentPage(pageFromPrev + 1);
        } else if (pageFromNext) {
          setCurrentPage(pageFromNext - 1);
        }
      } catch (err) {
        toast.error(err?.message || "Something went wrong fetching cards");
      } finally {
        setIsLoading(false);
      }
    },
    [
      cardsPerPage,
      ordering,
      search,
      columnFilters,
      API_BASE_URL,
      fixedFilters,
    ]
  );

  // Event handlers
  const handleOrderingChange = (e) => {
    const value = e.target.value;
    setOrdering(value);
    setIsAllPagesSelected(false);
    fetchCards(null, false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setIsAllPagesSelected(false);
    fetchCards(null, false);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleCardSelect = (cardId, isSelected) => {
    // Find the card to check if it's listed
    const card = cards.find((c) => c.id === cardId);

    // Prevent selecting listed cards
    if (isSelected && card && isCardLockedForEdit(card)) {
      toast.error("Listed or syncing cards cannot be selected");
      return;
    }

    if (isAllPagesSelected) {
      // When all pages are selected, track deselected cards
      if (isSelected) {
        // Re-selecting a card - remove it from deselected list
        setDeselectedCardIds((prev) => {
          const newDeselected = new Set(prev);
          newDeselected.delete(cardId);
          return newDeselected;
        });
      } else {
        // Deselecting a card - add it to deselected list
        setDeselectedCardIds((prev) => {
          const newDeselected = new Set(prev);
          newDeselected.add(cardId);
          return newDeselected;
        });
      }
      // Keep selectedCards in sync so the row checkbox and bulk actions
      // reflect the deselection immediately
      setSelectedCards((prev) => {
        const newSelected = new Set(prev);
        if (isSelected) {
          newSelected.add(cardId);
        } else {
          newSelected.delete(cardId);
        }
        return newSelected;
      });
    } else {
      // Normal individual selection
      setSelectedCards((prev) => {
        const newSelected = new Set(prev);
        if (isSelected) {
          newSelected.add(cardId);
        } else {
          newSelected.delete(cardId);
        }
        return newSelected;
      });
    }
  };

  const handleStatusFilterChange = (value) => {
    setColumnFilters((prev) => ({
      ...prev,
      status: value || "",
    }));
    setIsAllPagesSelected(false);
  };

  const handleOcrStatusFilterChange = (value) => {
    setColumnFilters((prev) => ({
      ...prev,
      ocr_status: value || "",
    }));
    setIsAllPagesSelected(false);
  };

  const handleCardsPerPageChange = (e) => {
    const newPageSize = parseInt(e.target.value);
    const url = new URL(window.location);
    url.searchParams.set("page_size", newPageSize.toString());
    window.history.pushState({}, "", url);
    setCurrentPage(1);
    setCardsPerPage(newPageSize);
  };

  const handleRefresh = () => {
    // Clear draftsOnly state when refresh button is clicked
    localStorage.removeItem("cardInspector_draftsOnly");
    
    fetchCards(null, false);
    
    // If in inspector mode, scroll to inspector view (same as when switching to inspector)
    if (batchViewMode === "inspector") {
      const scrollToInspector = () => {
        // Try to scroll the CardInspector into view first
        if (cardInspectorRef.current) {
          cardInspectorRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }

        // Also scroll window to bottom as fallback
        const scrollHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );

        window.scrollTo({
          top: scrollHeight,
          behavior: "smooth",
        });
      };

      // Try scrolling after delays to ensure DOM is ready
      setTimeout(() => {
        requestAnimationFrame(() => {
          scrollToInspector();
        });
      }, 100);

      setTimeout(() => {
        scrollToInspector();
      }, 500);

      setTimeout(() => {
        scrollToInspector();
      }, 1000);
    }
  };

  const handleClearFilters = () => {
    setOrdering("uploaded_at");
    setSearch("");
    setColumnFilters({
      status: "",
      ocr_status: "",
    });
    setIsAllPagesSelected(false);
    fetchCards(null, false);
  };

  const handleBack = () => {
    // If in inspector mode, switch to table view
    if (batchViewMode === "inspector") {
      prepareReturnToTableView();
      setBatchViewMode("overview");
      return;
    }
    
    // If in table mode (overview), navigate to batches
    if (batchViewMode === "overview") {
      if (fixedFilters && fixedFilters.batch__name) {
        navigate("/batches");
      } else {
        navigate(-1);
      }
      return;
    }
    
    // Fallback: navigate back
    navigate(-1);
  };

  const handleViewModeChange = (mode) => {
    if (mode === "overview" && batchViewMode === "inspector") {
      prepareReturnToTableView();
    }
    setBatchViewMode(mode);
  };

  const handleBulkEditModalOpen = (columnName) => {
    // Don't allow bulk edit if edit mode is disabled
    if (!isEditModeEnabled) return;

    // Check if any cards are selected
    if (selectedCards.size === 0 && !isAllPagesSelected) {
      toast.error("Select few cards to bulk edit");
      return;
    }

    // Prevent bulk edit for specific columns
    const nonBulkEditableColumns = [
      "title",
      "description",
      "card_name",
      "card_number",
      "ebay_title",
      "ebay_description",
      "ebay_card_name",
      "ebay_card_number",
    ];

    if (nonBulkEditableColumns.includes(columnName)) {
      return; // Don't open bulk edit modal for these columns
    }

    // Check if any selected cards are listed
    const selectedCardsArray = Array.from(selectedCards);
    const listedCards = cards.filter(
      (card) => selectedCardsArray.includes(card.id) && isCardLockedForEdit(card)
    );
    if (listedCards.length > 0) {
      toast.error(
        `Cannot bulk edit: ${listedCards.length} selected card(s) are listed and cannot be edited`
      );
      return;
    }

    setBulkEditData({
      columnName: columnName,
      newValue: "",
    });
    setIsBulkEditModalOpen(true);
  };

  const closeBulkEditModal = () => {
    setIsBulkEditModalOpen(false);
    setBulkEditData({
      columnName: "",
      newValue: "",
    });
  };

  const handleBulkReadyToList = async () => {
    if (selectedCards.size === 0 && !isAllPagesSelected) {
      toast.error("No cards selected");
      return;
    }

    const selectedIds = Array.from(selectedCards);
    const ids = selectedIds.filter((id) => {
      const card = cards.find((c) => c.id === id);
      return !card || !cardHasLongTitle(card);
    });
    const skippedLongTitle = selectedIds.length - ids.length;

    if (ids.length === 0) {
      toast.error(
        "Selected cards have titles longer than 80 characters. Shorten them before marking as ready."
      );
      return;
    }

    if (skippedLongTitle > 0) {
      toast.warn(
        `Skipped ${skippedLongTitle} card${
          skippedLongTitle === 1 ? "" : "s"
        } with titles longer than 80 characters.`
      );
    }

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // When all pages are selected, we need to get all card IDs from all pages
      // For now, we'll use the current page cards as a fallback
      const url = `${API_BASE_URL}/api/v1/cards/bulk-edit/`;

      const requestBody = {
        ids: ids,
        field: "status",
        value: "ready",
      };

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to mark cards as ready: ${response.status}`);
      }

      // Clear selected cards and refresh the data
      setSelectedCards(new Set());
      setIsAllSelected(false);
      setIsAllPagesSelected(false);
      setDeselectedCardIds(new Set());
      await fetchCards(null, false);

      const successCount = ids.length;
      toast.success(
        `Successfully marked ${successCount} card${
          successCount === 1 ? "" : "s"
        } as ready to list`
      );
    } catch (e) {
      console.error("Failed to mark cards as ready:", e);
      toast.error(e?.message || "Failed to mark cards as ready");
    }
  };

  const handleBulkMarkAsDraft = async () => {
    if (selectedCards.size === 0 && !isAllPagesSelected) {
      toast.error("No cards selected");
      return;
    }

    const selectedIds = Array.from(selectedCards);
    const ids = selectedIds.filter((id) => {
      const card = cards.find((c) => c.id === id);
      return !card || (card.status === "ready" && !isCardLockedForEdit(card));
    });

    if (ids.length === 0) {
      toast.error("No ready cards selected to move to draft");
      return;
    }

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const url = `${API_BASE_URL}/api/v1/cards/bulk-edit/`;

      const requestBody = {
        ids: ids,
        field: "status",
        value: "draft",
      };

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to mark cards as draft: ${response.status}`);
      }

      setSelectedCards(new Set());
      setIsAllSelected(false);
      setIsAllPagesSelected(false);
      setDeselectedCardIds(new Set());
      await fetchCards(null, false);

      const successCount = ids.length;
      toast.success(
        `Successfully marked ${successCount} card${
          successCount === 1 ? "" : "s"
        } as draft`
      );
    } catch (e) {
      console.error("Failed to mark cards as draft:", e);
      toast.error(e?.message || "Failed to mark cards as draft");
    }
  };

  const handleBulkEditChange = (field, value) => {
    setBulkEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBulkEditSubmit = async (e) => {
    e.preventDefault();
    // Don't allow bulk edit if edit mode is disabled
    if (!isEditModeEnabled) {
      toast.error(
        "Edit mode is disabled. Please enable edit mode to make changes."
      );
      return;
    }
    if (!bulkEditData.columnName || !bulkEditData.newValue) {
      toast.error("Please fill in the new value field");
      return;
    }

    try {
      if (selectedCards.size === 0) {
        toast.error("No cards selected for bulk edit");
        return;
      }

      // Check if any selected cards are listed
      const selectedCardsArray = Array.from(selectedCards);
      const listedCards = cards.filter(
        (card) =>
          selectedCardsArray.includes(card.id) && isCardLockedForEdit(card)
      );
      if (listedCards.length > 0) {
        toast.error(
          `Cannot bulk edit: ${listedCards.length} selected card(s) are listed and cannot be edited`
        );
        return;
      }

      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const ids = Array.from(selectedCards);
      const url = `${API_BASE_URL}/api/v1/cards/bulk-edit/`;

      const fieldMapping = getFieldMapping();
      const apiField =
        fieldMapping[bulkEditData.columnName] || bulkEditData.columnName;

      const requestBody = {
        ids: ids,
        field: apiField,
        value: bulkEditData.newValue,
      };

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to bulk edit cards: ${response.status}`);
      }

      // Refresh the data (keep selected cards selected)
      await fetchCards(null, false);

      toast.success(
        `Successfully updated ${bulkEditData.columnName} for ${
          ids.length
        } card${ids.length === 1 ? "" : "s"}`
      );
      closeBulkEditModal();
    } catch (e) {
      console.error("Failed to bulk edit cards:", e);
      toast.error(e?.message || "Failed to bulk edit cards");
    }
  };

  const openBulkDeleteModal = () => {
    if (selectedCards.size === 0) {
      alert("Please select cards to delete");
      return;
    }
    setIsBulkDeleteModalOpen(true);
  };

  const closeBulkDeleteModal = () => {
    setIsBulkDeleteModalOpen(false);
  };

  const openListOnEbayModal = async () => {
    // Must be connected to eBay before listing
    if (!isEbayConnected) {
      toast.error("Please connect to eBay before listing cards. Go to Connect to eBay to link your account.");
      return;
    }

    // Check if required policies are set
    if (requiredPolicies === false) {
      toast.error("Please set up your eBay policies before listing cards. Go to Policies page to configure them.");
      return;
    }
    
    // List button should list ALL ready cards, not just selected ones
    // Use actualReadyCardsCount which gets the actual count from the API
    if (!currentBatchId && actualReadyCardsCount === 0) {
      toast.error("No ready cards to list");
      return;
    }

    if (currentBatchId) {
      try {
        const preview = await getEbayBatchSyncPreview(
          currentBatchId,
          ebaySyncFilterInput,
          API_BASE_URL
        );
        setBatchSyncPreview(preview);
        setBatchListableCount(preview?.to_process ?? null);
        if ((preview?.to_process || 0) === 0) {
          toast.info(
            hasActiveCardFilters
              ? "No cards match the current filters to list on eBay."
              : "All cards in this batch are already listed on eBay."
          );
          return;
        }
      } catch (err) {
        console.error("Failed to load batch sync preview:", err);
        setBatchSyncPreview(null);
      }
    } else {
      setBatchSyncPreview(null);
    }

    setIsListOnEbayModalOpen(true);
  };

  const closeListOnEbayModal = () => {
    setIsListOnEbayModalOpen(false);
    setBatchSyncPreview(null);
  };

  const handleBulkDelete = async () => {
    try {
      if (selectedCards.size === 0 && !isAllPagesSelected) {
        alert("No cards selected for deletion");
        return;
      }

      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const ids = Array.from(selectedCards);
      const url = `${API_BASE_URL}/api/v1/cards/bulk-delete/`;

      const requestBody = { ids };

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to delete cards: ${response.status}`);
      }

      // Clear selected cards and refresh the data
      setSelectedCards(new Set());
      setIsAllSelected(false);
      setIsAllPagesSelected(false);
      setDeselectedCardIds(new Set());
      await fetchCards(null, false);

      const deleteCount = ids.length;
      toast.success(
        `Successfully deleted ${deleteCount} card${
          deleteCount === 1 ? "" : "s"
        }`
      );
      closeBulkDeleteModal();
    } catch (e) {
      console.error("Failed to delete cards:", e);
      toast.error(e?.message || "Failed to delete cards");
      closeBulkDeleteModal();
    }
  };

  const openAddCardModal = () => {
    openQuickAddModal();
  };

  const openQuickAddModal = () => {
    // Pre-fill batch name from current batch
    setQuickAddData((prev) => ({
      ...prev,
      batchName: batchName === "All Cards" ? "" : batchName,
      selectedFiles: [],
      uploadProgress: {},
      isUploading: false,
      error: "",
      uploadStatus: "",
    }));
    setIsQuickAddModalOpen(true);
    // Load presets list from API
    loadQuickAddPresets();
  };

  // Load presets list from API
  const loadQuickAddPresets = async () => {
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      let response = await fetch(
        `${API_BASE_URL}/api/v1/cards/preset/?all=true`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(
          `${API_BASE_URL}/api/v1/cards/preset/?all=true`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response.ok) {
        const data = await response.json();
        // Handle both array and paginated responses
        let presetsArray = [];
        if (Array.isArray(data)) {
          presetsArray = data;
        } else if (data.results && Array.isArray(data.results)) {
          presetsArray = data.results;
        } else if (data.id) {
          presetsArray = [data];
        }
        setQuickAddPresetsList(presetsArray);
      } else {
        console.error("Failed to load presets list:", response.statusText);
      }
    } catch (error) {
      console.error("Error loading presets list:", error);
    }
  };

  // Quick add preset management functions
  const handleQuickAddSetPresets = () => {
    setIsQuickAddPresetModalOpen(true);
  };

  const handleQuickAddClosePresetModal = () => {
    setIsQuickAddPresetModalOpen(false);
    setQuickAddPresetSearchQuery("");
  };

  const handleQuickAddSelectPreset = async (preset) => {
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // Fetch full preset data
      let response = await fetch(
        `${API_BASE_URL}/api/v1/cards/preset/${preset.id}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(
          `${API_BASE_URL}/api/v1/cards/preset/${preset.id}/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response.ok) {
        const presetData = await response.json();

        // Convert preset_data to the nested structure expected by quick add
        const convertedPresets = {
          Category: "",
          SuggestedPrice: "",
          Condition: "",
          Metadata: {
            "Card Game / Type": "",
            Set: "",
            SubSet: "",
            Variant: "",
            Year: "",
            Rarity: "",
            Edition: "",
            Autograph: "",
            Language: "",
            Graded: "",
            Grade: "",
            Condition: "",
            Producer: "",
            "Special Features": "",
            Damage: "",
            "Selling Points": "",
            "Market Value": "",
          },
          eBayListing: {
            Title: presetData.preset_data?.ebayTitle || "",
            Category: presetData.preset_data?.ebayCategory || "",
            Description: presetData.preset_data?.ebayDescription || "",
            Condition: presetData.preset_data?.ebayCondition || "",
            ItemSpecifics: {
              Brand: presetData.preset_data?.ebayBrand || "",
              "Card Name": presetData.preset_data?.ebayCardName || "",
              Set: presetData.preset_data?.ebaySet || "",
              SubSet: presetData.preset_data?.ebaySubset || "",
              Variant: presetData.preset_data?.ebayVariant || "",
              Year: presetData.preset_data?.ebayYear || "",
              Rarity: presetData.preset_data?.ebayRarity || "",
              Condition: presetData.preset_data?.ebayItemCondition || "",
              "Country/Region of Manufacture":
                presetData.preset_data?.ebayCountry || "",
              ...(CATEGORY_SPECIFIC_FIELD_LABEL[presetData.preset_data?.ebayCategory] && {
                [CATEGORY_SPECIFIC_FIELD_LABEL[presetData.preset_data?.ebayCategory]]:
                  presetData.preset_data?.ebaySportGameFranchise || "",
              }),
            },
          },
        };

        setQuickAddData((prev) => ({
          ...prev,
          presetsDraft: convertedPresets,
          defaultMetadata: convertedPresets,
        }));
        setQuickAddSelectedPreset(preset);
        setQuickAddBatchSpecificPresetName(""); // Clear batch-specific preset name when selecting from list
        setIsQuickAddPresetModalOpen(false);
        setQuickAddPresetSearchQuery("");
      }
    } catch (error) {
      console.error("Error loading preset:", error);
    }
  };

  const handleQuickAddClearPresets = () => {
    setQuickAddData((prev) => ({
      ...prev,
      defaultMetadata: null,
    }));
    setQuickAddSelectedPreset(null);
    setQuickAddBatchSpecificPresetName("");
  };

  // Batch-specific preset handlers
  const handleQuickAddOpenBatchSpecificPresetModal = () => {
    setIsQuickAddBatchSpecificPresetModalOpen(true);
  };

  const handleQuickAddCloseBatchSpecificPresetModal = () => {
    setIsQuickAddBatchSpecificPresetModalOpen(false);
    handleQuickAddResetBatchSpecificPresetForm();
  };

  const handleQuickAddBatchSpecificPresetInputChange = (field, value) => {
    setQuickAddBatchSpecificPresetFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleQuickAddResetBatchSpecificPresetForm = () => {
    setQuickAddBatchSpecificPresetFormData({
      presetName: "",
      category: "",
      sportGameFranchise: "",
      manufacturer: "",
      autographed: "",
      graded: "",
      condition: "",
      language: "",
      set: "",
      yearManufactured: "",
      vintage: "",
      conditionDescription: "",
      country: "",
    });
  };

  // Generate preview HTML from preset metadata
  const generateQuickAddPresetPreview = (metadata) => {
    if (!metadata) {
      return "No preset selected";
    }

    const escapeHTML = (text) => {
      if (!text) return "";
      return String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    };

    let previewHTML = "";

    // Basic fields
    if (metadata.Category) {
      previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>Category:</strong> ${escapeHTML(metadata.Category)}</div>`;
    }
    if (metadata.Condition) {
      previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>Condition:</strong> ${escapeHTML(metadata.Condition)}</div>`;
    }

    // eBay Listing fields
    if (metadata.eBayListing) {
      if (metadata.eBayListing.Category) {
        previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>eBay Category:</strong> ${escapeHTML(metadata.eBayListing.Category)}</div>`;
      }
      if (metadata.eBayListing.Condition) {
        previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>eBay Condition:</strong> ${escapeHTML(metadata.eBayListing.Condition)}</div>`;
      }
      if (metadata.eBayListing["Condition Description"]) {
        previewHTML += `<div style="margin-bottom: 0.5rem;"><strong>Condition Description:</strong> ${escapeHTML(metadata.eBayListing["Condition Description"])}</div>`;
      }

      // Item Specifics
      if (metadata.eBayListing.ItemSpecifics) {
        const itemSpecifics = metadata.eBayListing.ItemSpecifics;
        const filledSpecifics = Object.entries(itemSpecifics).filter(
          ([key, value]) => value && String(value).trim() !== ""
        );

        if (filledSpecifics.length > 0) {
          previewHTML += `<div style="margin-top: 0.75rem; margin-bottom: 0.5rem;"><strong>Item Specifics:</strong></div>`;
          filledSpecifics.forEach(([key, value]) => {
            previewHTML += `<div style="margin-left: 1rem; margin-bottom: 0.25rem;"><strong>${escapeHTML(key)}:</strong> ${escapeHTML(value)}</div>`;
          });
        }
      }
    }

    return previewHTML || "No preset data available";
  };

  const handleQuickAddSaveBatchSpecificPreset = () => {
    // Convert form data to the same structure as when selecting a preset
    const convertedPresets = {
      Category: quickAddBatchSpecificPresetFormData.category || "",
      SuggestedPrice: "",
      Condition: quickAddBatchSpecificPresetFormData.condition || "",
      Metadata: {
        "Card Game / Type": "",
        Set: quickAddBatchSpecificPresetFormData.set || "",
        SubSet: "",
        Variant: "",
        Year: quickAddBatchSpecificPresetFormData.yearManufactured || "",
        Rarity: "",
        Edition: "",
        Autograph: quickAddBatchSpecificPresetFormData.autographed || "",
        Language: quickAddBatchSpecificPresetFormData.language || "",
        Graded: quickAddBatchSpecificPresetFormData.graded || "",
        Grade: "",
        Condition: quickAddBatchSpecificPresetFormData.condition || "",
        Producer: quickAddBatchSpecificPresetFormData.manufacturer || "",
        "Special Features": "",
        Damage: "",
        "Selling Points": "",
        "Market Value": "",
      },
      eBayListing: {
        Category: quickAddBatchSpecificPresetFormData.category || "",
        Condition: quickAddBatchSpecificPresetFormData.condition || "",
        Description: "",
        "Condition Description":
          quickAddBatchSpecificPresetFormData.conditionDescription || "",
        ItemSpecifics: {
          Brand: quickAddBatchSpecificPresetFormData.manufacturer || "",
          "Card Name": "",
          Set: quickAddBatchSpecificPresetFormData.set || "",
          SubSet: "",
          Variant: "",
          Year: quickAddBatchSpecificPresetFormData.yearManufactured || "",
          "Year Manufactured":
            quickAddBatchSpecificPresetFormData.yearManufactured || "",
          "Card Number": "",
          Rarity: "",
          "Country/Region of Manufacture": quickAddBatchSpecificPresetFormData.country || "",
          Language: quickAddBatchSpecificPresetFormData.language || "",
          Autographed: quickAddBatchSpecificPresetFormData.autographed || "",
          Graded: quickAddBatchSpecificPresetFormData.graded || "",
          Vintage: quickAddBatchSpecificPresetFormData.vintage || "",
          ...(CATEGORY_SPECIFIC_FIELD_LABEL[quickAddBatchSpecificPresetFormData.category] && {
            [CATEGORY_SPECIFIC_FIELD_LABEL[quickAddBatchSpecificPresetFormData.category]]:
              quickAddBatchSpecificPresetFormData.sportGameFranchise || "",
          }),
        },
      },
    };

    setQuickAddData((prev) => ({
      ...prev,
      presetsDraft: convertedPresets,
      defaultMetadata: convertedPresets,
    }));
    setQuickAddSelectedPreset(null); // Clear selected preset since we're using batch-specific
    setQuickAddBatchSpecificPresetName(
      quickAddBatchSpecificPresetFormData.presetName || "Batch Specific Preset"
    );
    setIsQuickAddBatchSpecificPresetModalOpen(false);
    handleQuickAddResetBatchSpecificPresetForm();
  };

  // Filter presets based on search query
  const quickAddFilteredPresets = quickAddPresetsList.filter((preset) =>
    (preset.name || "")
      .toLowerCase()
      .includes(quickAddPresetSearchQuery.toLowerCase())
  );

  const closeQuickAddModal = () => {
    setIsQuickAddModalOpen(false);
    setIsTitleBuilderOpen(false);
    setQuickAddData({
      batchName: "",
      titleBuilder: [],
      selectedFiles: [],
      uploadProgress: {},
      isUploading: false,
      error: "",
      uploadStatus: "",
      isPresetsOpen: false,
      defaultMetadata: null,
      presetsDraft: {
        Category: "",
        SuggestedPrice: "",
        Condition: "",
        Metadata: {
          "Card Game / Type": "",
          Set: "",
          SubSet: "",
          Variant: "",
          Year: "",
          Rarity: "",
          Edition: "",
          Autograph: "",
          Language: "",
          Graded: "",
          Grade: "",
          Condition: "",
          Producer: "",
          "Special Features": "",
          Damage: "",
          "Selling Points": "",
          "Market Value": "",
        },
        eBayListing: {
          Category: "",
          Condition: "",
          ItemSpecifics: {
            Brand: "",
            "Card Name": "",
            Set: "",
            SubSet: "",
            Variant: "",
            Year: "",
            Rarity: "",
            Condition: "",
            "Country/Region of Manufacture": "",
          },
        },
      },
    });
    setQuickAddSelectedPreset(null);
    setQuickAddBatchSpecificPresetName("");
    handleQuickAddResetBatchSpecificPresetForm();
    setDraggedField(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDraggingOverDropZone(false);
    setEditingFieldIndex(null);
  };

  const handlePersonalizeModalOpen = () => {
    setIsPersonalizeTableModalOpen(true);
  };

  // Quick add form handlers
  const handleQuickAddChange = (field, value) => {
    setQuickAddData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Title Builder handlers
  const handleTitleBuilderDragStart = (e, field, index = null) => {
    setDraggedField(field);
    setDraggedIndex(index);
    e.dataTransfer.setData("text/plain", field);
    e.dataTransfer.effectAllowed = "move";
    if (e.target) {
      e.target.style.opacity = "0.5";
    }
  };

  const handleTitleBuilderDragEnd = (e) => {
    if (e.target) {
      e.target.style.opacity = "1";
    }
    setDraggedField(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDraggingOverDropZone(false);
  };

  const handleTitleBuilderDragOver = (e, index = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (index !== null) {
      setDragOverIndex(index);
    }
  };

  const handleTitleBuilderDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleTitleBuilderDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const field = e.dataTransfer.getData("text/plain");

    setQuickAddData((prev) => {
      const titleBuilder = prev.titleBuilder || [];
      // If dragging within the builder (reordering)
      if (draggedIndex !== null) {
        const newArray = [...titleBuilder];
        const [removed] = newArray.splice(draggedIndex, 1);
        // Adjust target index if dragging from before target
        const adjustedIndex =
          draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
        newArray.splice(adjustedIndex, 0, removed);
        return { ...prev, titleBuilder: newArray };
      } else {
        // If dragging from available fields
        const getFieldName = (item) =>
          typeof item === "string" ? item : item.field;
        const filtered = titleBuilder.filter(
          (item) => getFieldName(item) !== field
        );
        const newArray = [...filtered];
        newArray.splice(targetIndex, 0, { field, prefix: "", suffix: "" });
        return { ...prev, titleBuilder: newArray };
      }
    });

    setDraggedField(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleTitleBuilderDropToBuilder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const field = e.dataTransfer.getData("text/plain");

    setQuickAddData((prev) => {
      const titleBuilder = prev.titleBuilder || [];
      // Only add if not already in builder
      const getFieldName = (item) =>
        typeof item === "string" ? item : item.field;
      const fieldExists = titleBuilder.some(
        (item) => getFieldName(item) === field
      );
      if (!fieldExists) {
        return {
          ...prev,
          titleBuilder: [...titleBuilder, { field, prefix: "", suffix: "" }],
        };
      }
      return prev;
    });

    setDraggedField(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setIsDraggingOverDropZone(false);
  };

  // Handler to prevent drag and drop outside the TitleBuilderDropZone
  const handlePreventDropOutside = (e) => {
    // Check if the drop target is within the TitleBuilderDropZone
    const dropZone = titleBuilderDropZoneRef.current;
    if (!dropZone) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
      return;
    }

    const isOverDropZone = dropZone.contains(e.target);
    if (!isOverDropZone) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "none";
    }
  };

  const handleTitleBuilderRemoveField = (indexToRemove) => {
    setQuickAddData((prev) => ({
      ...prev,
      titleBuilder: (prev.titleBuilder || []).filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const handleTitleBuilderPrefixChange = (index, prefix) => {
    setQuickAddData((prev) => {
      const titleBuilder = prev.titleBuilder || [];
      const newArray = [...titleBuilder];
      const item = newArray[index];
      if (typeof item === "string") {
        newArray[index] = { field: item, prefix, suffix: "" };
      } else {
        const currentSuffix = typeof item === "string" ? "" : item.suffix || "";
        newArray[index] = { ...item, prefix, suffix: currentSuffix };
      }
      return { ...prev, titleBuilder: newArray };
    });
  };

  const handleTitleBuilderSuffixChange = (index, suffix) => {
    setQuickAddData((prev) => {
      const titleBuilder = prev.titleBuilder || [];
      const newArray = [...titleBuilder];
      const item = newArray[index];
      if (typeof item === "string") {
        newArray[index] = { field: item, prefix: "", suffix };
      } else {
        const currentPrefix = typeof item === "string" ? "" : item.prefix || "";
        newArray[index] = { ...item, prefix: currentPrefix, suffix };
      }
      return { ...prev, titleBuilder: newArray };
    });
  };

  const handleTitleBuilderFieldNameClick = (index, e) => {
    e.stopPropagation();
    setEditingFieldIndex(index);
  };

  const handleTitleBuilderInputBlur = (e) => {
    // Don't close if focus is moving to another input in the same field
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;

    // Check if focus is moving to another textarea in the same wrapper
    if (relatedTarget && currentTarget) {
      const wrapper = currentTarget.closest(".field-input-wrapper");
      if (wrapper && wrapper.contains(relatedTarget)) {
        // Focus is moving within the same wrapper, don't close
        return;
      }
    }

    // Small delay to allow focus to move between inputs
    setTimeout(() => {
      // Check if any input in this field still has focus
      const wrapper = e.target.closest(".field-input-wrapper");
      if (wrapper) {
        const hasFocus = wrapper.querySelector("textarea:focus");
        if (!hasFocus) {
          setEditingFieldIndex(null);
        }
      } else {
        setEditingFieldIndex(null);
      }
    }, 150);
  };

  const handleCloseTitleBuilder = () => {
    setIsTitleBuilderOpen(false);
  };

  // Auto-resize textarea when editing Title Builder
  useEffect(() => {
    if (editingFieldIndex !== null && isTitleBuilderOpen) {
      const textareas = document.querySelectorAll(
        `textarea[data-field-index="${editingFieldIndex}"]`
      );
      textareas.forEach((textarea) => {
        if (textarea) {
          textarea.style.height = "auto";
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      });
    }
  }, [editingFieldIndex, quickAddData.titleBuilder, isTitleBuilderOpen]);

  // Auto-resize field name textareas (non-editing mode)
  useEffect(() => {
    if (isTitleBuilderOpen) {
      const fieldNameTextareas =
        document.querySelectorAll("textarea[readonly]");
      fieldNameTextareas.forEach((textarea) => {
        if (
          textarea &&
          !textarea.closest(".field-input-wrapper") &&
          textarea.closest('[style*="z-index: 2000"]')
        ) {
          textarea.style.height = "auto";
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
      });
    }
  }, [quickAddData.titleBuilder, isTitleBuilderOpen]);

  const handleQuickAddFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setQuickAddData((prev) => ({
        ...prev,
        selectedFiles: files,
        error: "",
        uploadStatus: "",
      }));
    }
  };

  // Auto-scroll to upload button when files are added
  useEffect(() => {
    const filesCount = quickAddData.selectedFiles.length;
    if (filesCount > prevQuickAddFilesCountRef.current && filesCount > 0) {
      requestAnimationFrame(() => {
        quickAddActionsRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      });
    }
    prevQuickAddFilesCountRef.current = filesCount;
  }, [quickAddData.selectedFiles]);

  const handleQuickAddViewImage = (file) => {
    setQuickAddMagnifiedImage(URL.createObjectURL(file));
  };

  const handleQuickAddDeleteImage = (indexToRemove) => {
    setQuickAddData((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const closeQuickAddMagnifiedView = () => {
    if (quickAddMagnifiedImage) {
      URL.revokeObjectURL(quickAddMagnifiedImage);
    }
    setQuickAddMagnifiedImage(null);
  };

  // Extract error message from a fetch Response
  const extractErrorMessage = async (response) => {
    try {
      const text = await response.text();
      if (!text) return `${response.status} ${response.statusText}`;
      try {
        const data = JSON.parse(text);
        return (
          data?.error ||
          data?.message ||
          data?.detail ||
          (typeof data === "string" ? data : JSON.stringify(data))
        );
      } catch (_e) {
        return text;
      }
    } catch (_err) {
      return "Request failed";
    }
  };

  const handleQuickAddUpload = async () => {
    if (quickAddData.selectedFiles.length === 0) {
      setQuickAddData((prev) => ({
        ...prev,
        error: "Please select files to upload",
      }));
      return;
    }

    const filesToUpload = [...quickAddData.selectedFiles];
    const batchLabel = quickAddData.batchName || "Default Batch";
    const uploadId = crypto.randomUUID();
    const totalChunksAll = filesToUpload.reduce(
      (sum, file) => sum + Math.ceil(file.size / CHUNK_SIZE),
      0
    );

    closeQuickAddModal();

    startChunkedUpload({
      uploadId,
      batchName: batchLabel,
      totalChunks: totalChunksAll,
      fileCount: filesToUpload.length,
    });

    try {
      const token = localStorage.getItem("access");
      if (!token) throw new Error("No access token found. Please login again.");

      const { batchId } = await uploadFilesInChunks({
        files: filesToUpload,
        batchName: batchLabel,
        token,
        apiBaseUrl: API_BASE_URL,
        onChunkProgress: ({
          chunksUploaded,
          totalChunks,
          fileName,
          fileChunksUploaded,
          fileChunksTotal,
          serverProgress,
        }) => {
          updateChunkProgress(
            uploadId,
            chunksUploaded,
            totalChunks,
            serverProgress
          );
          setQuickAddData((prev) => ({
            ...prev,
            uploadProgress: {
              ...prev.uploadProgress,
              [fileName]: {
                uploaded: fileChunksUploaded,
                total: fileChunksTotal,
              },
            },
          }));
        },
      });

      const pruneEmptyDeep = (value) => {
        if (value == null) return undefined;
        if (typeof value === "string") {
          const trimmed = value.trim();
          return trimmed.length > 0 ? trimmed : undefined;
        }
        if (Array.isArray(value)) {
          const prunedArr = value
            .map(pruneEmptyDeep)
            .filter((v) => v !== undefined);
          return prunedArr.length > 0 ? prunedArr : undefined;
        }
        if (typeof value === "object") {
          const result = {};
          Object.entries(value).forEach(([k, v]) => {
            const pruned = pruneEmptyDeep(v);
            if (pruned !== undefined) result[k] = pruned;
          });
          return Object.keys(result).length > 0 ? result : undefined;
        }
        return value;
      };

      let defaultMetadata;
      if (quickAddData.defaultMetadata) {
        const metadataCopy = JSON.parse(
          JSON.stringify(quickAddData.defaultMetadata)
        );
        if (metadataCopy.Category !== undefined) {
          metadataCopy.Category = "";
        }
        if (metadataCopy.eBayListing?.Category !== undefined) {
          metadataCopy.eBayListing.Category = "";
        }
        defaultMetadata = pruneEmptyDeep(metadataCopy);
      }

      const titleBuilder = quickAddData.titleBuilder || [];
      const titleBuilderData = titleBuilder
        .map((item) => {
          const fieldName = typeof item === "string" ? item : item.field;
          const prefix = typeof item === "string" ? "" : item.prefix || "";
          const suffix = typeof item === "string" ? "" : item.suffix || "";
          const parts = [];
          if (prefix) parts.push({ type: "string", value: prefix });
          parts.push({ type: "field-name", value: fieldName });
          if (suffix) parts.push({ type: "string", value: suffix });
          return parts;
        })
        .flat();

      const finalizeData = await finalizeCardUpload({
        batchName: batchLabel,
        defaultMetadata,
        titleBuilderData,
        token,
        apiBaseUrl: API_BASE_URL,
      });

      const resolvedBatchId = finalizeData?.batch_id ?? batchId;
      if (resolvedBatchId == null) {
        throw new Error("Server did not return a batch_id for this upload.");
      }

      startBatchProcessing({
        uploadId,
        batchId: resolvedBatchId,
        taskId: finalizeData?.task_id,
        batchName: batchLabel,
        status: finalizeData?.status || "processing",
        progress: finalizeData?.progress ?? 50,
      });

      sessionStorage.setItem("fromQuickAdd", "true");
    } catch (err) {
      setQuickAddData((prev) => ({
        ...prev,
        error: err.message || "Upload failed",
      }));
    } finally {
      setQuickAddData((prev) => ({
        ...prev,
        isUploading: false,
      }));
    }
  };

  const handlePersonalizeModalClose = async () => {
    // Save to API when Apply Changes is clicked (only if batch ID is available)
    if (currentBatchId) {
      try {
        const success = await saveBatchTablePersonalize(
          currentBatchId,
          columnOrder,
          columnVisibility
        );
        if (success) {
          toast.success("Table personalization saved successfully!");
        } else {
          toast.error("Failed to save table personalization");
        }
      } catch (error) {
        console.error("Error saving table personalization:", error);
        toast.error("Failed to save table personalization");
      }
    }
    setIsPersonalizeTableModalOpen(false);
  };

  // Quick add preset management functions

  const handleQuickAddPresetChange = (path, value) => {
    setQuickAddData((prev) => {
      const newDraft = JSON.parse(JSON.stringify(prev.presetsDraft));
      const keys = path.split(".");
      let current = newDraft;

      // Navigate to the parent object
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key] || typeof current[key] !== "object") {
          current[key] = {};
        }
        current = current[key];
      }

      // Set the final value
      current[keys[keys.length - 1]] = value;
      return {
        ...prev,
        presetsDraft: newDraft,
      };
    });
  };

  const handleQuickAddApplyPresets = () => {
    setQuickAddData((prev) => ({
      ...prev,
      defaultMetadata: prev.presetsDraft,
      isPresetsOpen: false,
    }));
  };

  const handleQuickAddCancelPresets = () => {
    setQuickAddData((prev) => ({
      ...prev,
      isPresetsOpen: false,
    }));
  };

  const handleColumnVisibilityChange = (columnKey) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleSelectAllColumns = () => {
    setColumnVisibility((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = true;
      });
      return newState;
    });
  };

  const handleDeselectAllColumns = () => {
    setColumnVisibility((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });
      return newState;
    });
  };

  const PQ_LOG = "[eBay:PriceQty]";

  const handleCellClick = (card, fieldName, event) => {
    event.stopPropagation();
    console.log(PQ_LOG, "[Table] Cell clicked", {
      cardId: card.id,
      fieldName,
      status: card.status,
    });
    // Track the last-viewed card so switching to inspector mode (e.g. via the
    // view mode tabs) lands on the same card the user was looking at
    setInspectorSelectedCard(card);

    // If edit mode is disabled, switch to inspector mode and select the card
    if (!isEditModeEnabled) {
      setBatchViewMode("inspector");
      return;
    }

    // Don't allow editing if card is listed or syncing - except quantity/price,
    // which can still be pushed live to eBay for already-listed cards.
    if (
      isCardLockedForEdit(card) &&
      !canEditListedCardField(card, fieldName)
    ) {
      console.log(PQ_LOG, "[Table] Blocked - card is locked and field is not live-editable", {
        cardId: card.id,
        fieldName,
        status: card.status,
      });
      toast.error("Listed or syncing cards cannot be edited");
      return;
    }

    if (canEditListedCardField(card, fieldName)) {
      console.log(PQ_LOG, "[Table] Card is listed but field is live-editable - allowing edit", {
        cardId: card.id,
        fieldName,
      });
    }

    // Resolve field name using mapping
    const fieldMapping = getFieldMapping();
    const resolvedFieldName = fieldMapping[fieldName] || fieldName;
    const currentValue = getFieldValue(card, fieldName);
    setEditingCell(`${card.id}-${resolvedFieldName}`);
    setEditValue(currentValue || "");
  };

  const handleCellEdit = (value) => {
    setEditValue(value);
  };

  const isValidPriceInput = (value) => {
    if (value === "") return true;
    if (!/^\d*\.?\d*$/.test(value)) return false;
    return (value.match(/\./g) || []).length <= 1;
  };

  const handlePriceCellEdit = (value) => {
    if (isValidPriceInput(value)) {
      setEditValue(value);
    }
  };

  const handleCellSave = async (card, fieldName, valueOverride = null) => {
    console.log(PQ_LOG, "[Table] handleCellSave called", {
      cardId: card.id,
      fieldName,
      valueOverride,
      status: card.status,
    });
    try {
      const isSalePriceField =
        fieldName === "sale_price" || fieldName === "ebay_listing.SalePrice";
      const isQuantityField =
        fieldName === "available_quantity" ||
        fieldName === "ebay_listing.AvailableQuantity" ||
        fieldName === "metadata.available_quantity";
      const isQuantityOrPriceField = isSalePriceField || isQuantityField;

      // Don't allow saving if card is listed or syncing - except quantity/price,
      // which push the change live to eBay instead of just the local DB.
      if (
        isCardLockedForEdit(card) &&
        !(card.status === "listed" && isQuantityOrPriceField)
      ) {
        console.log(PQ_LOG, "[Table] Save blocked - card locked, field not live-editable", {
          cardId: card.id,
          fieldName,
        });
        toast.error("Listed or syncing cards cannot be edited");
        setEditingCell(null);
        setEditValue("");
        return;
      }

      // Use valueOverride if provided, otherwise use editValue
      let valueToSave = valueOverride !== null ? valueOverride : editValue;

      // Get the current value before making changes
      const currentValue = getFieldValue(card, fieldName);
      let newValue = valueToSave?.toString().trim() || "";
      const oldValue = currentValue?.toString().trim() || "";

      if (isSalePriceField && newValue !== "") {
        if (newValue.endsWith(".")) {
          newValue = newValue.slice(0, -1);
        }
        if (!/^\d+(\.\d+)?$/.test(newValue) || parseFloat(newValue) < 0) {
          toast.error("Enter a valid price (0 or positive, one decimal point)");
          return;
        }
        valueToSave = newValue;
      }

      // If values are the same, just close editing without showing any toast
      if (newValue === oldValue) {
        setEditingCell(null);
        setEditValue("");
        return;
      }

      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // Use field mapping to<｜place▁holder▁no▁197｜> resolve the actual field path
      const fieldMapping = getFieldMapping();
      const actualFieldPath = fieldMapping[fieldName] || fieldName;

      // Always update ebay_listing (source of truth)
      const ebayPath = getEbayListingPath(fieldName);

      // Build the update payload - always update ebay_listing
      const updatePayload = {
        metadata: {
          ...card.metadata,
          ebay_listing: {
            ...card.metadata?.ebay_listing,
            ItemSpecifics: {
              ...card.metadata?.ebay_listing?.ItemSpecifics,
            },
          },
        },
      };

      // Update ebay_listing based on the path
      if (ebayPath.startsWith("ebay_listing.ItemSpecifics.")) {
        const itemField = ebayPath.replace("ebay_listing.ItemSpecifics.", "");
        updatePayload.metadata.ebay_listing.ItemSpecifics[itemField] =
          valueToSave;

        // Special handling for Condition - also update top-level Condition
        if (itemField === "Condition") {
          updatePayload.metadata.ebay_listing.Condition = valueToSave;
        }

        // Special handling for Manufacturer - also update Brand
        if (itemField === "Manufacturer") {
          updatePayload.metadata.ebay_listing.ItemSpecifics.Brand = valueToSave;
        }
      } else if (ebayPath.startsWith("ebay_listing.")) {
        const ebayField = ebayPath.replace("ebay_listing.", "");
        updatePayload.metadata.ebay_listing[ebayField] = valueToSave;

        // Special handling for Condition - also update ItemSpecifics.Condition
        if (ebayField === "Condition") {
          updatePayload.metadata.ebay_listing.ItemSpecifics.Condition =
            valueToSave;
        }
      }

      // Handle non-ebay fields (status, id, etc.) - update directly
      if (actualFieldPath === "status") {
        updatePayload[actualFieldPath] = valueToSave;
      }

      // Handle condition field - update in metadata and sync to ebay_listing
      if (
        actualFieldPath === "condition" ||
        fieldName === "ebay_condition" ||
        fieldName === "condition"
      ) {
        updatePayload.metadata.condition = valueToSave;
        // Also sync to ebay_listing.Condition
        updatePayload.metadata.ebay_listing.Condition = valueToSave;
      }

      // Handle category field - update in metadata and sync to ebay_listing
      if (
        actualFieldPath === "category" ||
        fieldName === "ebay_category" ||
        fieldName === "category"
      ) {
        updatePayload.metadata.category = valueToSave;
        // Also sync to ebay_listing.Category
        updatePayload.metadata.ebay_listing.Category = valueToSave;
      }

      // Handle available_quantity - update in ebay_listing
      if (
        actualFieldPath === "metadata.available_quantity" ||
        fieldName === "available_quantity"
      ) {
        updatePayload.metadata.ebay_listing.AvailableQuantity = valueToSave;
      }

      const isTitleField =
        fieldName === "ebay_title" ||
        fieldName === "title" ||
        fieldName === "ebay_listing.Title" ||
        ebayPath === "ebay_listing.Title";

      // Handle title field - update in ebay_listing.Title and set isTitleEdited flag
      if (isTitleField) {
        // Calculate default title from title builder config
        const defaultTitle = formatTitleBuilderConfig(card);
        
        if (newValue === "") {
          // Empty title means reset to default - clear isTitleEdited flag and set default title builder value
          updatePayload.isTitleEdited = false;
          updatePayload.metadata.ebay_listing.Title = defaultTitle;
        } else {
          // Compare edited value with default title builder value
          const editedValueTrimmed = newValue.trim();
          const defaultTitleTrimmed = defaultTitle.trim();
          
          // If edited value matches default, reset the flag; otherwise set it to true
          if (editedValueTrimmed === defaultTitleTrimmed) {
            updatePayload.isTitleEdited = false;
            updatePayload.metadata.ebay_listing.Title = defaultTitle;
          } else {
            updatePayload.isTitleEdited = true;
            updatePayload.metadata.ebay_listing.Title = valueToSave;
          }
        }

        const savedTitle = (
          updatePayload.metadata?.ebay_listing?.Title ?? ""
        ).toString();
        if (
          savedTitle.trim().length > LONG_TITLE_MAX_LENGTH &&
          card.status === "ready"
        ) {
          updatePayload.status = "draft";
        }
      }

      const url = `${API_BASE_URL}/api/v1/cards/${card.id}/`;
      console.log(PQ_LOG, "[Table] Saving locally - PATCH request", {
        url,
        updatePayload,
      });
      let response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatePayload),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        });
      }

      if (!response.ok) {
        console.error(PQ_LOG, "[Table] Local PATCH failed", { status: response.status });
        throw new Error(`Failed to update card: ${response.status}`);
      }

      const updatedCard = await response.json();
      console.log(PQ_LOG, "[Table] Local PATCH response", updatedCard);

      // Update the card in the current list
      setCards((prevCards) =>
        prevCards.map((c) => (c.id === card.id ? { ...c, ...updatedCard } : c))
      );

      // Update the selected card in inspector if it's the one being edited
      if (inspectorSelectedCard?.id === card.id) {
        setInspectorSelectedCard((prev) => ({ ...prev, ...updatedCard }));
      }

      if (isTitleField) {
        void refreshListAfterTitleEdit();
      } else if (currentBatchId) {
        void fetchBatchListableCount();
      }

      toast.success("Card updated successfully");
      setEditingCell(null);
      setEditValue("");
    } catch (error) {
      console.error(PQ_LOG, "[Table] Error updating card", error);
      toast.error("Failed to update card");
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  // Mapping from display field names to ebay_listing paths (source of truth)
  const getEbayListingPath = (fieldName) => {
    const fieldMapping = getFieldMapping();
    const actualFieldPath = fieldMapping[fieldName] || fieldName;

    // Map all fields to their ebay_listing equivalents
    const ebayListingMapping = {
      // Basic fields -> ebay_listing top level
      title: "ebay_listing.Title",
      ebay_title: "ebay_listing.Title",
      description: "ebay_listing.Description",
      category: "ebay_listing.Category",
      condition: "ebay_listing.Condition",

      // Detailed metadata -> ebay_listing.ItemSpecifics
      "detailed_metadata.Card Name": "ebay_listing.ItemSpecifics.Card Name",
      "detailed_metadata.Set": "ebay_listing.ItemSpecifics.Set",
      "detailed_metadata.SubSet": "ebay_listing.ItemSpecifics.SubSet",
      "detailed_metadata.Variant": "ebay_listing.ItemSpecifics.Variant",
      "detailed_metadata.Year": "ebay_listing.ItemSpecifics.Year",
      "detailed_metadata.Rarity": "ebay_listing.ItemSpecifics.Rarity",
      "detailed_metadata.Condition": "ebay_listing.ItemSpecifics.Condition",
      "detailed_metadata.Producer": "ebay_listing.ItemSpecifics.Manufacturer",
      "detailed_metadata.Card Number": "ebay_listing.ItemSpecifics.Card Number",
      "detailed_metadata.Language": "ebay_listing.ItemSpecifics.Language",
      "detailed_metadata.Autograph": "ebay_listing.ItemSpecifics.Autographed",
      "detailed_metadata.Graded": "ebay_listing.ItemSpecifics.Graded",
      "detailed_metadata.Vintage": "ebay_listing.ItemSpecifics.Vintage",
      "detailed_metadata.Card Game / Type": "ebay_listing.ItemSpecifics.Type",
      "detailed_metadata.Year Manufactured":
        "ebay_listing.ItemSpecifics.Year Manufactured",
    };

    // If already an ebay_listing path, return as is
    if (actualFieldPath.startsWith("ebay_listing.")) {
      return actualFieldPath;
    }

    // Map to ebay_listing path
    return ebayListingMapping[actualFieldPath] || actualFieldPath;
  };

  const getFieldValue = (card, fieldName) => {
    // Handle title field - check isTitleEdited flag
    if (fieldName === "ebay_title") {
      if (card.isTitleEdited === true) {
        // If edited, return the edited title from metadata.ebay_listing.Title
        return card.metadata?.ebay_listing?.Title;
      } else {
        // If not edited, return formatted title from title builder config
        return formatTitleBuilderConfig(card);
      }
    }

    // Handle condition field first - read from metadata.condition
    if (fieldName === "ebay_condition" || fieldName === "condition") {
      return card.metadata?.condition;
    }

    // Handle category field - read from metadata.category
    if (fieldName === "ebay_category" || fieldName === "category") {
      return card.metadata?.category;
    }

    // Handle available_quantity - read from ebay_listing
    const fieldMapping = getFieldMapping();
    const actualFieldPath = fieldMapping[fieldName] || fieldName;
    if (
      actualFieldPath === "metadata.available_quantity" ||
      fieldName === "available_quantity"
    ) {
      return card.metadata?.ebay_listing?.AvailableQuantity;
    }

    // Always read from ebay_listing (source of truth)
    const ebayPath = getEbayListingPath(fieldName);

    if (ebayPath.startsWith("ebay_listing.ItemSpecifics.")) {
      const itemField = ebayPath.replace("ebay_listing.ItemSpecifics.", "");
      return card.metadata?.ebay_listing?.ItemSpecifics?.[itemField];
    } else if (ebayPath.startsWith("ebay_listing.")) {
      const ebayField = ebayPath.replace("ebay_listing.", "");
      return card.metadata?.ebay_listing?.[ebayField];
    }

    // Fallback for non-ebay fields (status, id, etc.)
    if (
      actualFieldPath === "status" ||
      actualFieldPath === "id" ||
      actualFieldPath === "batch" ||
      actualFieldPath === "uploaded_at" ||
      actualFieldPath === "condition" ||
      actualFieldPath === "category"
    ) {
      return card[actualFieldPath] || card.metadata?.[actualFieldPath];
    }

    return null;
  };

  // Label for the category-specific field (Sport/Game/Franchise) shown after a category is selected
  const CATEGORY_SPECIFIC_FIELD_LABEL = {
    "Sport Trading Card": "Sport",
    "Non-Sport Trading Card": "Franchise",
    "CCG Individual Card": "Game",
    "Collectible Sports Sticker Singles": "Sport",
  };

  // Get condition options based on category
  const getConditionOptions = (category) => {
    if (category === "CCG Individual Card") {
      return [
        "Near Mint or Better",
        "Lightly Played (Excellent)",
        "Moderately Played (Very Good)",
        "Heavily Played (Poor)",
      ];
    } else if (
      category === "Sport Trading Card" ||
      category === "Non-Sport Trading Card" ||
      category === "Collectible Sports Sticker Singles" ||
      category === "Collectible Non-Sport Sticker Singles"
    ) {
      return ["Near Mint or Better", "Excellent", "Very Good", "Poor"];
    } else {
      // Default: show all options if category is not set or unknown
      return [
        "Near Mint or Better",
        "Lightly Played (Excellent)",
        "Moderately Played (Very Good)",
        "Heavily Played (Poor)",
        "Excellent",
        "Very Good",
        "Poor",
      ];
    }
  };

  const handleImageClick = (card, imageUrl) => {
    // Set the card to show both front and back images in the modal
    setSelectedCardForModal(card);
  };

  const handleCloseImageModal = () => {
    setSelectedCardForModal(null);
  };

  const handleImageLoadStart = (cardId, imageType) => {
    setImageLoading((prev) => ({
      ...prev,
      [`${cardId}-${imageType}`]: true,
    }));

    // Set a timeout to ensure loader shows for at least 3 seconds
    setTimeout(() => {
      setImageLoading((prev) => ({
        ...prev,
        [`${cardId}-${imageType}`]: false,
      }));
      setImageLoaded((prev) => ({
        ...prev,
        [`${cardId}-${imageType}`]: true,
      }));
    }, 3000);
  };

  const handleImageLoad = (cardId, imageType) => {
    // Don't immediately hide the loader - let the timeout handle it
    // This ensures the loader always shows for at least 3 seconds
    console.log(`Image loaded: ${cardId}-${imageType}`);
  };

  const handleImageError = (cardId, imageType) => {
    setImageLoading((prev) => ({
      ...prev,
      [`${cardId}-${imageType}`]: false,
    }));
  };

  const handleStatusUpdate = async (cardId, newStatus) => {
    try {
      // Find the card to check if it's listed
      const card = cards.find((c) => c.id === cardId);
      if (card && isCardLockedForEdit(card)) {
        toast.error("Listed or syncing cards cannot be edited");
        return;
      }

      // Only allow changing status to "ready" or "draft"
      // "listed" status can only be set by eBay listing, not manually
      if (newStatus === "listed") {
        toast.error(
          "Listed status can only be set when card is listed on eBay"
        );
        return;
      }

      if (newStatus === "ready" && cardHasLongTitle(card)) {
        toast.error(
          "This card's title exceeds 80 characters. Shorten it before marking as ready to list."
        );
        return;
      }

      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const url = `${API_BASE_URL}/api/v1/cards/${cardId}/`;
      let response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: newStatus }),
        });
      }

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(`Failed to update status: ${response.status} ${txt}`);
      }

      const updatedCard = await response.json();

      setCards((prev) =>
        prev.map((c) => (c.id === cardId ? { ...c, ...updatedCard } : c))
      );

      if (inspectorSelectedCard?.id === cardId) {
        setInspectorSelectedCard((prev) => ({ ...prev, ...updatedCard }));
      }

      toast.success("Status updated");
      
      // Refresh the table after status update
      await fetchCards(null, false);
      await fetchReadyCardsCount();
      await fetchBatchListableCount();
    } catch (e) {
      console.error("Error updating status:", e);
      toast.error(e?.message || "Failed to update status");
    }
  };

  const handleRefreshCard = async (cardId) => {
    // Prevent duplicate requests for the same row
    if (refreshingCardIds.has(cardId)) return;

    setRefreshingCardIds((prev) => {
      const next = new Set(prev);
      next.add(cardId);
      return next;
    });

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const url = `${API_BASE_URL}/api/v1/cards/${cardId}/refresh/`;
      let response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
        });
      }

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(`Failed to refresh card: ${response.status} ${txt}`);
      }

      const responseData = await response.json().catch(() => null);
      const refreshedCard =
        responseData?.card || responseData?.data || responseData;

      if (refreshedCard && typeof refreshedCard === "object") {
        setCards((prev) =>
          prev.map((c) => (c.id === cardId ? { ...c, ...refreshedCard } : c))
        );

        if (inspectorSelectedCard?.id === cardId) {
          setInspectorSelectedCard((prev) => ({ ...prev, ...refreshedCard }));
        }
      } else {
        // Fallback if API returns no body: keep updates row-scoped with lightweight fetch
        let refetchToken = localStorage.getItem("access");
        if (!refetchToken) refetchToken = await refreshAccessToken();

        let refetchResponse = await fetch(`${API_BASE_URL}/api/v1/cards/${cardId}/`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${refetchToken}`,
            "Content-Type": "application/json",
          },
        });

        if (refetchResponse.status === 401) {
          const newToken = await refreshAccessToken();
          refetchResponse = await fetch(`${API_BASE_URL}/api/v1/cards/${cardId}/`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${newToken}`,
              "Content-Type": "application/json",
            },
          });
        }

        if (refetchResponse.ok) {
          const cardData = await refetchResponse.json();
          setCards((prev) =>
            prev.map((c) => (c.id === cardId ? { ...c, ...cardData } : c))
          );

          if (inspectorSelectedCard?.id === cardId) {
            setInspectorSelectedCard((prev) => ({ ...prev, ...cardData }));
          }
        }
      }

      toast.success("Card refreshed");
    } catch (e) {
      console.error("Error refreshing card:", e);
      toast.error(e?.message || "Failed to refresh card");
    } finally {
      setRefreshingCardIds((prev) => {
        const next = new Set(prev);
        next.delete(cardId);
        return next;
      });
    }
  };

  const handleRowClick = (card, type) => {
    // Track the last-viewed card so switching to inspector mode (e.g. via the
    // view mode tabs) lands on the same card the user was looking at
    setInspectorSelectedCard(card);

    // If edit mode is disabled, switch to inspector mode and select the card
    if (!isEditModeEnabled) {
      setBatchViewMode("inspector");
      return;
    }
    console.log("Row clicked:", card, type);
    // Handle row click logic here
  };

  const renderEditableCell = (card, fieldName, value, isTextarea = false) => {
    // Resolve field name to match what's passed to onCellClick
    const fieldMapping = getFieldMapping();
    const resolvedFieldName = fieldMapping[fieldName] || fieldName;
    const cellKey = `${card.id}-${resolvedFieldName}`;
    const isEditing = editingCell === cellKey;

    // Check if this is a category field
    const isCategoryField =
      fieldName === "category" ||
      fieldName === "ebay_category" ||
      resolvedFieldName === "category" ||
      resolvedFieldName === "ebay_listing.Category";

    // Check if this is a condition field
    const isConditionField =
      fieldName === "condition" ||
      fieldName === "ebay_condition" ||
      resolvedFieldName === "condition" ||
      resolvedFieldName === "ebay_listing.Condition" ||
      resolvedFieldName === "detailed_metadata.Condition" ||
      resolvedFieldName === "ebay_listing.ItemSpecifics.Condition";

    // Check if this is a yes/no field (graded, vintage, autographed)
    const isYesNoField =
      fieldName === "graded" ||
      fieldName === "autograph" ||
      fieldName === "ebay_graded" ||
      fieldName === "ebay_vintage" ||
      fieldName === "ebay_autographed" ||
      resolvedFieldName === "detailed_metadata.Graded" ||
      resolvedFieldName === "detailed_metadata.Autograph" ||
      resolvedFieldName === "ebay_listing.ItemSpecifics.Graded" ||
      resolvedFieldName === "ebay_listing.ItemSpecifics.Vintage" ||
      resolvedFieldName === "ebay_listing.ItemSpecifics.Autographed";

    // Check if this is a number field
    const isNumberField =
      fieldName === "available_quantity" ||
      fieldName === "sale_price" ||
      resolvedFieldName === "metadata.available_quantity" ||
      resolvedFieldName === "ebay_listing.SalePrice";

    // Check if this is a price field (for EUR formatting)
    const isPriceField =
      fieldName === "sale_price" ||
      resolvedFieldName === "ebay_listing.SalePrice";

    if (isEditing) {
      if (isTextarea) {
        return (
          <textarea
            value={editValue}
            onChange={(e) => handleCellEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) {
                e.preventDefault();
                handleCellSave(card, resolvedFieldName);
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleCellCancel();
              }
            }}
            onBlur={() => handleCellSave(card, resolvedFieldName)}
            autoFocus
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "2px solid #3b82f6",
              borderRadius: "4px",
              fontSize: "inherit",
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: "60px",
            }}
          />
        );
      } else if (isYesNoField) {
        return (
          <select
            value={editValue}
            onChange={(e) => {
              const newValue = e.target.value;
              handleCellEdit(newValue);
              // Save immediately when a value is selected (not empty)
              if (newValue && newValue !== "") {
                handleCellSave(card, resolvedFieldName, newValue);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                handleCellCancel();
              }
            }}
            onBlur={() => {
              // Only save if a value was selected
              if (editValue && editValue !== "") {
                handleCellSave(card, resolvedFieldName);
              } else {
                handleCellCancel();
              }
            }}
            autoFocus
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "2px solid #3b82f6",
              borderRadius: "4px",
              fontSize: "inherit",
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        );
      } else if (isCategoryField) {
        return (
          <select
            value={editValue}
            onChange={(e) => {
              const newValue = e.target.value;
              handleCellEdit(newValue);
              // Save immediately when a value is selected (not empty)
              if (newValue && newValue !== "") {
                handleCellSave(card, resolvedFieldName, newValue);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                handleCellCancel();
              }
            }}
            onBlur={() => {
              // Only save if a value was selected
              if (editValue && editValue !== "") {
                handleCellSave(card, resolvedFieldName);
              } else {
                handleCellCancel();
              }
            }}
            autoFocus
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "2px solid #3b82f6",
              borderRadius: "4px",
              fontSize: "inherit",
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            <option value="">Select Category</option>
            <option value="Sport Trading Card">Sport Trading Card</option>
            <option value="Non-Sport Trading Card">
              Non-Sport Trading Card
            </option>
            <option value="CCG Individual Card">CCG Individual Card</option>
            <option value="Collectible Sports Sticker Singles">Collectible Sports Sticker Singles</option>
            <option value="Collectible Non-Sport Sticker Singles">Collectible Non-Sport Sticker Singles</option>
          </select>
        );
      } else if (isConditionField) {
        // Get category from card to determine condition options
        const category =
          getFieldValue(card, "category") ||
          getFieldValue(card, "ebay_category");
        const conditionOptions = getConditionOptions(category);

        return (
          <select
            value={editValue}
            onChange={(e) => {
              const newValue = e.target.value;
              handleCellEdit(newValue);
              // Save immediately when a value is selected (not empty)
              if (newValue && newValue !== "") {
                handleCellSave(card, resolvedFieldName, newValue);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                handleCellCancel();
              }
            }}
            onBlur={() => {
              // Only save if a value was selected
              if (editValue && editValue !== "") {
                handleCellSave(card, resolvedFieldName);
              } else {
                handleCellCancel();
              }
            }}
            autoFocus
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "2px solid #3b82f6",
              borderRadius: "4px",
              fontSize: "inherit",
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            <option value="">Select Condition</option>
            {conditionOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      } else if (isPriceField) {
        return (
          <input
            type="text"
            inputMode="decimal"
            value={editValue}
            onChange={(e) => handlePriceCellEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCellSave(card, resolvedFieldName);
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleCellCancel();
              }
            }}
            onBlur={() => handleCellSave(card, resolvedFieldName)}
            autoFocus
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "2px solid #3b82f6",
              borderRadius: "4px",
              fontSize: "inherit",
              fontFamily: "inherit",
            }}
            placeholder="0.00"
          />
        );
      } else if (isNumberField) {
        return (
          <input
            type="number"
            value={editValue}
            onChange={(e) => handleCellEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCellSave(card, resolvedFieldName);
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleCellCancel();
              }
            }}
            onBlur={() => handleCellSave(card, resolvedFieldName)}
            autoFocus
            min="0"
            step="1"
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "2px solid #3b82f6",
              borderRadius: "4px",
              fontSize: "inherit",
              fontFamily: "inherit",
            }}
          />
        );
      } else {
        return (
          <input
            type="text"
            value={editValue}
            onChange={(e) => handleCellEdit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCellSave(card, resolvedFieldName);
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleCellCancel();
              }
            }}
            onBlur={() => handleCellSave(card, resolvedFieldName)}
            autoFocus
            style={{
              width: "100%",
              padding: "4px 8px",
              border: "2px solid #3b82f6",
              borderRadius: "4px",
              fontSize: "inherit",
              fontFamily: "inherit",
            }}
          />
        );
      }
    }

    // Format price fields with EUR currency
    let displayValue = value || "N/A";
    if (isPriceField && value) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        displayValue = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      }
    }

    // Check if value is empty, "n/a", or "unknown" (case-insensitive)
    const normalizedValue =
      typeof value === "string" ? value.trim().toLowerCase() : "";
    const isNA =
      !value || normalizedValue === "n/a" || normalizedValue === "unknown";

    if (isNA) {
      return (
        <div
          style={{
            cursor: "pointer",
            backgroundColor: "#fef2f2",
            color: "#b91c1c",
            fontWeight: 600,
            border: "1px solid #fecaca",
            borderRadius: "6px",
            margin: "-0.75rem -1.25rem",
            padding: "0.75rem 1.25rem",
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
            width: "calc(100% + 2.5rem)",
            minHeight: "100%",
          }}
        >
          <AlertTriangle size={14} style={{ flexShrink: 0 }} />
          {displayValue}
        </div>
      );
    }

    return <span style={{ cursor: "pointer" }}>{displayValue}</span>;
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setColumnOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }

    setActiveId(null);
  };

  // Save column order to localStorage whenever it changes (API saving happens on Apply Changes)
  useEffect(() => {
    try {
      localStorage.setItem(
        "dashboard_columnOrder",
        JSON.stringify(columnOrder)
      );
    } catch (error) {
      console.error("Error saving column order to localStorage:", error);
    }
  }, [columnOrder]);

  // Save column visibility to localStorage whenever it changes (API saving happens on Apply Changes)
  useEffect(() => {
    try {
      localStorage.setItem(
        "dashboard_columnVisibility",
        JSON.stringify(columnVisibility)
      );
    } catch (error) {
      console.error("Error saving column visibility to localStorage:", error);
    }
  }, [columnVisibility]);

  useEffect(() => {
    fetchBatchListableCount();
  }, [fetchBatchListableCount]);

  // Load presets from API on component mount
  useEffect(() => {
    const loadPresets = async () => {
      try {
        const token = localStorage.getItem("access");
        if (!token) return;

        const response = await fetch(
          `${API_BASE_URL}/api/v1/cards/preset/?all=true`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.preset_data) {
            // Convert the flat preset_data to the nested structure expected by upload
            const convertedPresets = {
              Category: data.preset_data.category || "",
              SuggestedPrice: data.preset_data.suggestedPrice || "",
              Condition: data.preset_data.condition || "",
              Metadata: {
                "Card Game / Type": data.preset_data.cardGameType || "",
                Set: data.preset_data.set || "",
                SubSet: data.preset_data.ebaySubset || "",
                Variant: data.preset_data.ebayVariant || "",
                Year: data.preset_data.year || "",
                Rarity: data.preset_data.ebayRarity || "",
                Edition: "",
                Autograph: "",
                Language: "",
                Graded: "",
                Grade: "",
                Condition: data.preset_data.ebayItemCondition || "",
                Producer: data.preset_data.producer || "",
                "Special Features": "",
                Damage: "",
                "Selling Points": "",
                "Market Value": "",
              },
              eBayListing: {
                Category: data.preset_data.ebayCategory || "",
                Condition: data.preset_data.ebayCondition || "",
                ItemSpecifics: {
                  Brand: data.preset_data.ebayBrand || "",
                  "Card Name": data.preset_data.ebayCardName || "",
                  Set: data.preset_data.ebaySet || "",
                  SubSet: data.preset_data.ebaySubset || "",
                  Variant: data.preset_data.ebayVariant || "",
                  Year: data.preset_data.ebayYear || "",
                  Rarity: data.preset_data.ebayRarity || "",
                  Condition: data.preset_data.ebayItemCondition || "",
                  "Country/Region of Manufacture":
                    data.preset_data.ebayCountry || "",
                },
              },
            };
            setQuickAddData((prev) => ({
              ...prev,
              presetsDraft: convertedPresets,
            }));
          }
        } else {
          console.error("Failed to load presets:", response.statusText);
        }
      } catch (error) {
        console.error("Error loading presets:", error);
      }
    };

    loadPresets();
  }, [API_BASE_URL]);

  // Helper function to get field value from card - always read from ebay_listing (source of truth)
  const getFieldValueFromCard = (card, fieldName) => {
    const fieldMapping = {
      "Card Name":
        card?.metadata?.ebay_listing?.ItemSpecifics?.["Card Name"] || "",
      Type: card?.metadata?.ebay_listing?.ItemSpecifics?.["Type"] || "",
      Manufacturer:
        card?.metadata?.ebay_listing?.ItemSpecifics?.["Manufacturer"] || "",
      Set: card?.metadata?.ebay_listing?.ItemSpecifics?.["Set"] || "",
      "Card Number":
        card?.metadata?.ebay_listing?.ItemSpecifics?.["Card Number"] || "",
      "Year Manufactured":
        card?.metadata?.ebay_listing?.ItemSpecifics?.["Year Manufactured"] ||
        "",
    };
    return fieldMapping[fieldName] || "";
  };

  // Helper function to format title builder config (matching TableRow logic)
  const formatTitleBuilderConfig = (card) => {
    if (
      !batchTitleBuilderConfig ||
      !batchTitleBuilderConfig.configuration ||
      batchTitleBuilderConfig.configuration.length === 0
    ) {
      return (
        card?.metadata?.ebay_listing?.Title || card?.metadata?.title || "N/A"
      );
    }

    // New format: [{ type: "field-name" | "string", value: string }, ...]
    let title = "";

    batchTitleBuilderConfig.configuration.forEach((item) => {
      if (item.type === "field-name") {
        // Get field value from card
        const fieldValue = getFieldValueFromCard(card, item.value || "");
        if (fieldValue) {
          title += fieldValue;
        }
      } else if (item.type === "string") {
        // Add string value directly (preserves spaces)
        title += item.value || "";
      }
    });

    // Trim and return, or fallback to default title
    const finalTitle = title.trim();
    return (
      finalTitle ||
      card?.metadata?.ebay_listing?.Title ||
      card?.metadata?.title ||
      "N/A"
    );
  };

  // Console log rendered titles when both cards and config are available
  useEffect(() => {
    if (cards.length > 0 && batchTitleBuilderConfig) {
      // Log rendered title for first card
      const firstCard = cards[0];
      const renderedTitle = formatTitleBuilderConfig(firstCard);
      console.log("=== RENDERED TITLE (first card) ===", renderedTitle);

      // Log the title building process
      if (
        batchTitleBuilderConfig.configuration &&
        batchTitleBuilderConfig.configuration.length > 0
      ) {
        const titleParts = [];
        batchTitleBuilderConfig.configuration.forEach((item, index) => {
          if (item.type === "field-name") {
            const fieldValue = getFieldValueFromCard(
              firstCard,
              item.value || ""
            );
            titleParts.push({
              index,
              type: "field-name",
              fieldName: item.value,
              apiValue: fieldValue,
              used: fieldValue ? true : false,
            });
          } else if (item.type === "string") {
            titleParts.push({
              index,
              type: "string",
              value: item.value,
            });
          }
        });
        console.log("=== TITLE BUILDING PROCESS ===", titleParts);
      }
    }
  }, [cards, batchTitleBuilderConfig]);

  // Effects
  useEffect(() => {
    fetchCards();
    fetchReadyCardsCount();
    fetchBatchListableCount();
  }, [fetchCards, fetchReadyCardsCount, fetchBatchListableCount]);

  // Reset edit mode when batch changes
  useEffect(() => {
    // Reset edit mode when batch ID actually changes (not on initial mount)
    if (
      prevBatchIdRef.current !== currentBatchId &&
      prevBatchIdRef.current !== undefined
    ) {
      setIsEditModeEnabled(true);
    }
    prevBatchIdRef.current = currentBatchId;
  }, [currentBatchId]);

  // Subscribe to batch OCR completion progress for the current batch
  useEffect(() => {
    setBatchOcrProgress(null);

    if (!currentBatchId) return;

    const unsubscribe = subscribeBatchOcrProgress(
      currentBatchId,
      {
        onProgress: (payload) => {
          setBatchOcrProgress(payload);
        },
        onComplete: (payload) => {
          setBatchOcrProgress(payload);
          fetchCards();
          fetchReadyCardsCount();
          fetchBatchListableCount();
          toastify("success", payload?.message || "All OCR processing completed");
        },
      },
      API_BASE_URL
    );

    return () => {
      unsubscribe();
    };
  }, [
    currentBatchId,
    API_BASE_URL,
    fetchCards,
    fetchReadyCardsCount,
    fetchBatchListableCount,
  ]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("page_size")) {
      const url = new URL(window.location);
      url.searchParams.set("page_size", cardsPerPage.toString());
      window.history.replaceState({}, "", url);
    }
  }, [cardsPerPage]);

  // Calculate count of ready cards - always show total across all pages
  // The List button lists ALL ready cards, so it should always show the total count
  const readyCardsCount = (() => {
    // Calculate total ready cards using ratio method
    if (cards.length === 0) return 0;
    const readyCardsOnPage = cards.filter(
      (card) => card.status === "ready"
    ).length;
    const readyRatio = readyCardsOnPage / cards.length;
    // Estimate total ready cards across all pages
    return Math.round(count * readyRatio);
  })();

  // Calculate count of selected cards that are NOT already ready (for Ready button)
  const notReadySelectedCardsCount = (() => {
    if (isAllPagesSelected) {
      // When all pages selected, calculate total non-ready cards
      if (cards.length === 0) return 0;
      const nonReadyCardsOnPage = cards.filter(
        (card) =>
          card.status !== "ready" &&
          !isCardLockedForEdit(card) &&
          !cardHasLongTitle(card)
      ).length;
      const nonReadyRatio = nonReadyCardsOnPage / cards.length;
      // Estimate total non-ready cards across all pages
      const nonReadyTotal = Math.round(count * nonReadyRatio);
      // Reduce by manually deselected cards (estimated using the same ratio)
      const deselectedNonReady = Math.round(
        deselectedCardIds.size * nonReadyRatio
      );
      return Math.max(0, nonReadyTotal - deselectedNonReady);
    } else {
      // Normal selection - count selected cards that are NOT already ready
      const selectedCardsArray = Array.from(selectedCards);
      return cards.filter(
        (card) =>
          selectedCardsArray.includes(card.id) &&
          card.status !== "ready" &&
          !isCardLockedForEdit(card) &&
          !cardHasLongTitle(card)
      ).length;
    }
  })();

  // Calculate count of selected cards that ARE ready (for Draft button)
  const readySelectedCardsCount = (() => {
    if (isAllPagesSelected) {
      if (cards.length === 0) return 0;
      const readyCardsOnPage = cards.filter(
        (card) => card.status === "ready" && !isCardLockedForEdit(card)
      ).length;
      const readyRatio = readyCardsOnPage / cards.length;
      const readyTotal = Math.round(count * readyRatio);
      // Reduce by manually deselected cards (estimated using the same ratio)
      const deselectedReady = Math.round(deselectedCardIds.size * readyRatio);
      return Math.max(0, readyTotal - deselectedReady);
    } else {
      const selectedCardsArray = Array.from(selectedCards);
      return cards.filter(
        (card) =>
          selectedCardsArray.includes(card.id) &&
          card.status === "ready" &&
          !isCardLockedForEdit(card)
      ).length;
    }
  })();

  // Calculate count of non-listed cards (for select all counter)
  const nonListedCardsCount = cards.filter(
    (card) => !isCardLockedForEdit(card)
  ).length;

  const showTitleLengthFilter =
    (hasLongTitles && longTitleCount > 0) ||
    columnFilters.status === STATUS_FILTER_TITLE_LENGTH;

  useEffect(() => {
    // Only consider non-listed cards for selection
    const nonListedCards = cards.filter((card) => !isCardLockedForEdit(card));
    const allNonListedCardIds = new Set(nonListedCards.map((card) => card.id));
    const isAllCurrentPageCardsSelected =
      allNonListedCardIds.size > 0 &&
      [...allNonListedCardIds].every((id) => selectedCards.has(id));

    // Update the header checkbox state. When "select all pages" is active,
    // the header should only appear checked if nothing has been explicitly
    // deselected, so manual deselects are reflected immediately.
    const shouldShowChecked = isAllPagesSelected
      ? deselectedCardIds.size === 0
      : isAllCurrentPageCardsSelected;
    setIsAllSelected(shouldShowChecked);

    // If all pages are selected, automatically select non-listed cards on the
    // current page that haven't been explicitly deselected (e.g. cards on a
    // newly loaded page).
    if (isAllPagesSelected && allNonListedCardIds.size > 0) {
      const idsToSelect = [...allNonListedCardIds].filter(
        (id) => !deselectedCardIds.has(id) && !selectedCards.has(id)
      );
      if (idsToSelect.length > 0) {
        const newSelected = new Set(selectedCards);
        idsToSelect.forEach((id) => newSelected.add(id));
        setSelectedCards(newSelected);
      }
    }
  }, [selectedCards, cards, isAllPagesSelected, deselectedCardIds]);

  // Ref for CardInspector to scroll to it
  const cardInspectorRef = useRef(null);
  const inspectorPageSelectRef = useRef("first");

  // Refs/state for scrolling to and highlighting a table row when returning from inspector mode
  const tableRowRefs = useRef({});
  const pendingHighlightCardIdRef = useRef(null);
  const highlightTimeoutRef = useRef(null);
  const [highlightedCardId, setHighlightedCardId] = useState(null);

  // Table view and inspector view now share the same page/page size, so the
  // currently inspected card is already on the right page when switching back
  const prepareReturnToTableView = useCallback(() => {
    if (!inspectorSelectedCard) return;
    pendingHighlightCardIdRef.current = inspectorSelectedCard.id;
  }, [inspectorSelectedCard]);

  const scrollToAndHighlightCard = useCallback((cardId) => {
    if (!cardId) return;
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    setHighlightedCardId(cardId);
    requestAnimationFrame(() => {
      const el = tableRowRefs.current[cardId];
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedCardId(null);
    }, 3500); // matches the 5x 0.7s pulse animation
  }, []);

  // After returning to table view from inspector, scroll to and highlight the
  // card that was being viewed in the inspector
  useEffect(() => {
    if (batchViewMode === "overview" && pendingHighlightCardIdRef.current) {
      const cardId = pendingHighlightCardIdRef.current;
      pendingHighlightCardIdRef.current = null;
      if (cards.find((c) => c.id === cardId)) {
        scrollToAndHighlightCard(cardId);
      }
    }
  }, [batchViewMode, cards, scrollToAndHighlightCard]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  const handleInspectorFetchPage = useCallback(
    (url, selectPosition = "first") => {
      if (!url) return;
      inspectorPageSelectRef.current = selectPosition;
      fetchCards(url, false);
    },
    [fetchCards]
  );

  const refreshInspectorList = useCallback(() => {
    fetchCards(currentListUrlRef.current ?? null, false);
  }, [fetchCards]);

  // Auto-select card in inspector mode after page load or when selection is invalid
  useEffect(() => {
    if (batchViewMode === "inspector" && cards.length > 0) {
      if (
        !inspectorSelectedCard ||
        !cards.find((card) => card.id === inspectorSelectedCard.id)
      ) {
        const selectPosition = inspectorPageSelectRef.current;
        const index = selectPosition === "last" ? cards.length - 1 : 0;
        setInspectorSelectedCard(cards[index]);
        inspectorPageSelectRef.current = "first";
      }
    }
  }, [batchViewMode, cards, inspectorSelectedCard]);

  // Reload page when data loads after navigating from upload page
  useEffect(() => {
    const fromUpload = sessionStorage.getItem("fromUpload");
    if (fromUpload === "true" && !isLoading && cards.length > 0) {
      // Wait for table to be fully rendered using requestAnimationFrame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            sessionStorage.removeItem("fromUpload");
            window.location.reload();
          }, 2000); // Wait 2 seconds before reload
        });
      });
    }
  }, [isLoading, cards.length]);

  // Reload page when data loads after Quick Add upload
  useEffect(() => {
    const fromQuickAdd = sessionStorage.getItem("fromQuickAdd");
    if (fromQuickAdd === "true" && !isLoading && cards.length > 0) {
      // Wait for table to be fully rendered using requestAnimationFrame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            sessionStorage.removeItem("fromQuickAdd");
            window.location.reload();
          }, 3000); // Wait 2 seconds before reload
        });
      });
    }
  }, [isLoading, cards.length]);

  // Auto-scroll to bottom when inspector view mode is activated
  useEffect(() => {
    if (batchViewMode === "inspector") {
      // Use requestAnimationFrame and multiple attempts to ensure scroll happens
      const scrollToBottom = () => {
        // Try to scroll the CardInspector into view first
        if (cardInspectorRef.current) {
          cardInspectorRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }

        // Also scroll window to bottom as fallback
        const scrollHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );

        window.scrollTo({
          top: scrollHeight,
          behavior: "smooth",
        });
      };

      // Try scrolling after delays to ensure DOM is ready
      setTimeout(() => {
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      }, 100);

      setTimeout(() => {
        scrollToBottom();
      }, 500);

      setTimeout(() => {
        scrollToBottom();
      }, 1000);
    }
  }, [batchViewMode]);

  const handleListOnEbay = async () => {
    setIsListOnEbayModalOpen(false);
    setIsListingOnEbay(true);

    try {
      let payload;

      if (currentBatchId) {
        payload = buildSelectAllSyncPayload(currentBatchId, ebaySyncFilterInput);
      } else {
        let readyCardIds = [];
        let token = localStorage.getItem("access");
        if (!token) token = await refreshAccessToken();

        let allReadyCards = [];
        let nextUrl = buildBaseListUrl(API_BASE_URL, {
          ordering,
          search,
          fixedFilters,
          columnFilters: { ...columnFilters, status: "ready" },
          batchViewMode,
          cardsPerPage: 100,
        });

        while (nextUrl) {
          let response = await fetch(nextUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (response.status === 401) {
            token = await refreshAccessToken();
            response = await fetch(nextUrl, {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            });
          }

          if (!response.ok) {
            throw new Error(`Failed to fetch ready cards: ${response.status}`);
          }

          const data = await response.json();
          const pageCards = Array.isArray(data.results) ? data.results : [];
          allReadyCards = [...allReadyCards, ...pageCards];
          nextUrl = data.next ? normalizePageUrl(data.next, API_BASE_URL) : null;
        }

        const listableReadyCards = allReadyCards.filter(
          (card) => !cardHasLongTitle(card)
        );
        const excludedLongTitleCount =
          allReadyCards.length - listableReadyCards.length;
        if (excludedLongTitleCount > 0) {
          toast.warn(
            `Excluded ${excludedLongTitleCount} ready card${
              excludedLongTitleCount === 1 ? "" : "s"
            } with titles longer than 80 characters.`
          );
        }

        readyCardIds = listableReadyCards.map((card) => card.id);
        if (readyCardIds.length === 0) {
          toast.error(
            hasLongTitles
              ? "No listable ready cards. Shorten titles longer than 80 characters first."
              : "No ready cards to list"
          );
          return;
        }

        payload = { card_ids: readyCardIds };
      }

      const data = await startEbaySync(payload, API_BASE_URL);
      const message =
        data?.message ||
        (data?.status === "completed"
          ? "All cards already listed on eBay"
          : "eBay sync started");

      toast.success(message);

      if (data?.sync_run_id) {
        navigate(EBAY_SYNC_DETAIL.replace(":syncRunId", data.sync_run_id));
      }

      await fetchCards(null, false);
      await fetchBatchListableCount();
    } catch (error) {
      console.error("Error listing cards on eBay:", error);
      toast.error(error?.message || "Failed to list cards on eBay");
    } finally {
      setIsListingOnEbay(false);
      setBatchSyncPreview(null);
    }
  };

  // Title Builder Modal handlers
  const handleTitleBuilderModalOpen = () => {
    setIsTitleBuilderModalOpen(true);
    // Determine default type based on batch configuration
    // If batch config exists and is_same_as_global/global is true, use global
    // If batch config exists and is_same_as_global/global is false, use batch
    // If no batch config or no batch selected, default to global
    let defaultType = "global";
    if (currentBatchId && batchTitleBuilderConfig) {
      // Check both is_same_as_global and global properties (API might return either)
      const isGlobal =
        batchTitleBuilderConfig.is_same_as_global ??
        batchTitleBuilderConfig.global ??
        false;
      defaultType = isGlobal ? "global" : "batch";
    }
    setTitleBuilderType(defaultType);
    setTitleBuilderConfig([]);
  };

  const parseTitleBuilderConfiguration = (configuration) => {
    if (!configuration || !Array.isArray(configuration)) {
      return [];
    }

    // New format: [{ type: "field-name" | "string", value: string }, ...]
    // Convert to internal format for backward compatibility with existing UI
    const result = [];
    let currentItem = null;

    configuration.forEach((item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        if (item.type === "field-name") {
          // If there's a pending item, save it
          if (currentItem) {
            result.push(currentItem);
          }
          // Start new item with field and default space suffix
          currentItem = {
            field: (item.value || "").trim(),
            prefix: "",
            suffix: " ", // Default space after each field
          };
        } else if (item.type === "string") {
          const stringValue = item.value || "";
          // Check if this is just a space (default space after field)
          const isSpaceOnly = stringValue.trim() === "" && stringValue.length > 0;
          
          if (currentItem) {
            // If we have a field, add string as suffix
            // If it's just a space and suffix is already default space, keep it
            // Otherwise append the string value
            if (isSpaceOnly && currentItem.suffix === " ") {
              // Already has default space, keep it
            } else if (isSpaceOnly) {
              // Set as space if not already set
              currentItem.suffix = " ";
            } else {
              // Append non-space content
              currentItem.suffix = (currentItem.suffix || " ") + stringValue;
            }
          } else {
            // If no field yet, add as prefix
            currentItem = {
              field: "",
              prefix: stringValue,
              suffix: "",
            };
          }
        }
      }
    });

    // Add the last item if exists
    if (currentItem && currentItem.field) {
      result.push(currentItem);
    } else if (currentItem && currentItem.prefix) {
      // If only prefix exists, add it
      result.push(currentItem);
    }

    return result.filter((item) => item.field || item.prefix || item.suffix);
  };

  const loadTitleBuilderConfiguration = useCallback(async () => {
    // For batch type, require currentBatchId
    if (titleBuilderType === "batch" && !currentBatchId) {
      setTitleBuilderConfig([]);
      setTitleBuilderIsLoading(false);
      return;
    }

    setTitleBuilderIsLoading(true);
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // If global type, hit global endpoint; otherwise hit batch-specific API
      const url =
        titleBuilderType === "global"
          ? `${API_BASE_URL}/api/v1/cards/title-builder/`
          : `${API_BASE_URL}/api/v1/cards/batches/${currentBatchId}/title-builder/`;

      let response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
        });
      }

      if (response.ok) {
        const data = await response.json();
        if (data.configuration && Array.isArray(data.configuration)) {
          const parsedConfig = parseTitleBuilderConfiguration(
            data.configuration
          );
          setTitleBuilderConfig(parsedConfig);
        } else {
          setTitleBuilderConfig([]);
        }
      } else if (response.status === 404) {
        setTitleBuilderConfig([]);
      }
    } catch (err) {
      console.error("Error loading title builder configuration:", err);
      setTitleBuilderConfig([]);
    } finally {
      setTitleBuilderIsLoading(false);
    }
  }, [titleBuilderType, currentBatchId, API_BASE_URL]);

  // Load configuration when modal opens or type changes
  useEffect(() => {
    if (isTitleBuilderModalOpen) {
      loadTitleBuilderConfiguration();
    }
  }, [
    isTitleBuilderModalOpen,
    titleBuilderType,
    currentBatchId,
    loadTitleBuilderConfiguration,
  ]);

  const handleTitleBuilderTypeChange = (type) => {
    setTitleBuilderType(type);
  };

  const handleModalTitleBuilderDragStart = (e, field, index = null) => {
    setTitleBuilderDraggedField(field);
    setTitleBuilderDraggedIndex(index);
    e.dataTransfer.setData("text/plain", field);
    e.dataTransfer.effectAllowed = "move";
    if (e.target) {
      e.target.style.opacity = "0.5";
    }
  };

  const handleModalTitleBuilderDragEnd = (e) => {
    if (e.target) {
      e.target.style.opacity = "1";
    }
    setTitleBuilderDraggedField(null);
    setTitleBuilderDraggedIndex(null);
    setTitleBuilderDragOverIndex(null);
    setTitleBuilderIsDraggingOverDropZone(false);
  };

  const handleModalTitleBuilderDragOver = (e, index = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (index !== null) {
      setTitleBuilderDragOverIndex(index);
    }
  };

  const handleModalTitleBuilderDragLeave = () => {
    setTitleBuilderDragOverIndex(null);
  };

  const handleModalTitleBuilderDrop = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const field = e.dataTransfer.getData("text/plain");

    setTitleBuilderConfig((prev) => {
      if (titleBuilderDraggedIndex !== null) {
        const newArray = [...prev];
        const [removed] = newArray.splice(titleBuilderDraggedIndex, 1);
        const adjustedIndex =
          titleBuilderDraggedIndex < targetIndex
            ? targetIndex - 1
            : targetIndex;
        newArray.splice(adjustedIndex, 0, removed);
        return newArray;
      } else {
        const getFieldName = (item) =>
          typeof item === "string" ? item : item.field;
        const filtered = prev.filter((item) => getFieldName(item) !== field);
        const newArray = [...filtered];
        newArray.splice(targetIndex, 0, { field, prefix: "", suffix: " " });
        return newArray;
      }
    });

    setTitleBuilderDraggedField(null);
    setTitleBuilderDraggedIndex(null);
    setTitleBuilderDragOverIndex(null);
  };

  const handleModalTitleBuilderDropToBuilder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const field = e.dataTransfer.getData("text/plain");

    setTitleBuilderConfig((prev) => {
      const getFieldName = (item) =>
        typeof item === "string" ? item : item.field;
      const fieldExists = prev.some((item) => getFieldName(item) === field);
      if (!fieldExists) {
        return [...prev, { field, prefix: "", suffix: " " }];
      }
      return prev;
    });

    setTitleBuilderDraggedField(null);
    setTitleBuilderDraggedIndex(null);
    setTitleBuilderDragOverIndex(null);
    setTitleBuilderIsDraggingOverDropZone(false);
  };

  const handleModalTitleBuilderRemoveField = (indexToRemove) => {
    setTitleBuilderConfig((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const handleModalTitleBuilderPrefixChange = (index, prefix) => {
    setTitleBuilderConfig((prev) => {
      const newArray = [...prev];
      const item = newArray[index];
      if (typeof item === "string") {
        newArray[index] = { field: item, prefix, suffix: "" };
      } else {
        const currentSuffix = typeof item === "string" ? "" : item.suffix || "";
        newArray[index] = { ...item, prefix, suffix: currentSuffix };
      }
      return newArray;
    });
  };

  const handleModalTitleBuilderSuffixChange = (index, suffix) => {
    setTitleBuilderConfig((prev) => {
      const newArray = [...prev];
      const item = newArray[index];
      if (typeof item === "string") {
        newArray[index] = { field: item, prefix: "", suffix };
      } else {
        const currentPrefix = typeof item === "string" ? "" : item.prefix || "";
        newArray[index] = { ...item, prefix: currentPrefix, suffix };
      }
      return newArray;
    });
  };

  const handleModalTitleBuilderFieldNameClick = (index, e) => {
    e.stopPropagation();
    setTitleBuilderEditingFieldIndex(index);
  };

  const handleModalTitleBuilderInputBlur = (e) => {
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;

    if (relatedTarget && currentTarget) {
      const wrapper = currentTarget.closest(".field-input-wrapper");
      if (wrapper && wrapper.contains(relatedTarget)) {
        return;
      }
    }

    setTimeout(() => {
      const wrapper = e.target.closest(".field-input-wrapper");
      if (wrapper) {
        const hasFocus = wrapper.querySelector("textarea:focus");
        if (!hasFocus) {
          setTitleBuilderEditingFieldIndex(null);
        }
      } else {
        setTitleBuilderEditingFieldIndex(null);
      }
    }, 150);
  };

  const formatTitleBuilderConfiguration = () => {
    // Convert internal format to new API format: [{ type: "field-name" | "string", value: string }, ...]
    const result = [];

    titleBuilderConfig.forEach((item) => {
      const fieldName = typeof item === "string" ? item : item.field;
      const prefix = typeof item === "string" ? "" : (item.prefix || "").trim();
      const suffix = typeof item === "string" ? "" : (item.suffix || "");

      // Add prefix as string if exists
      if (prefix) {
        result.push({
          type: "string",
          value: prefix,
        });
      }

      // Add field name
      if (fieldName) {
        result.push({
          type: "field-name",
          value: fieldName,
        });
        
        // Always add a space after field-name (like TitleBuilder.jsx does)
        result.push({
          type: "string",
          value: " ",
        });
      }

      // Add suffix as string if exists (only if it has non-whitespace content)
      // Note: We already added the default space above, so only add suffix if it has additional content
      if (suffix && suffix.trim() !== "") {
        result.push({
          type: "string",
          value: suffix,
        });
      }
    });

    return result;
  };

  const handleTitleBuilderSave = async () => {
    if (titleBuilderConfig.length === 0) {
      toast.error("Please add at least one field to save the configuration.");
      return;
    }

    // For batch type, require currentBatchId
    if (titleBuilderType === "batch" && !currentBatchId) {
      toast.error(
        "No batch selected. Cannot save batch-specific configuration."
      );
      return;
    }

    setTitleBuilderIsSaving(true);

    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      const configuration = formatTitleBuilderConfiguration();
      const payload = {
        configuration: configuration,
      };

      // If global type, hit global endpoint; otherwise hit batch-specific API
      const url =
        titleBuilderType === "global"
          ? `${API_BASE_URL}/api/v1/cards/title-builder/`
          : `${API_BASE_URL}/api/v1/cards/batches/${currentBatchId}/title-builder/`;

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        toast.success("Title builder settings saved successfully!");
        setIsTitleBuilderModalOpen(false);
        // Refresh the batch title builder config if batch-specific
        if (titleBuilderType === "batch" && currentBatchId) {
          await fetchBatchTitleBuilder(currentBatchId);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData.detail ||
          errorData.message ||
          `Failed to save settings: ${response.statusText}`;
        toast.error(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        err.message || "Failed to save settings. Please try again.";
      toast.error(errorMessage);
    } finally {
      setTitleBuilderIsSaving(false);
    }
  };

  const handleSaveCard = async (cardId, formData) => {
    console.log(PQ_LOG, "[Inspector] handleSaveCard called", {
      cardId,
      status: formData.status,
      AvailableQuantity: formData.ebay_listing?.AvailableQuantity,
      SalePrice: formData.ebay_listing?.SalePrice,
    });
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessToken();

      // Transform formData to match API expectations
      // Exclude available_quantity from top-level metadata since it's stored in ebay_listing.AvailableQuantity
      // Also extract isTitleEdited as it's a top-level field
      const { available_quantity, isTitleEdited, ...formDataWithoutExtracted } =
        formData;
      const updatePayload = {
        metadata: {
          ...formDataWithoutExtracted,
          detailed_metadata: formData.detailed_metadata || {},
          ebay_listing: formData.ebay_listing || {},
        },
      };

      // Add isTitleEdited at top level if it exists
      if (formData.hasOwnProperty('isTitleEdited')) {
        updatePayload.isTitleEdited = formData.isTitleEdited;
      }

      const url = `${API_BASE_URL}/api/v1/cards/${cardId}/`;
      console.log(PQ_LOG, "[Inspector] Saving locally - PATCH request", {
        url,
        updatePayload,
      });
      let response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatePayload),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessToken();
        response = await fetch(url, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatePayload),
        });
      }

      if (!response.ok) {
        console.error(PQ_LOG, "[Inspector] Local PATCH failed", { status: response.status });
        throw new Error(`Failed to update card: ${response.status}`);
      }

      const updatedCard = await response.json();
      console.log(PQ_LOG, "[Inspector] Local PATCH response", updatedCard);

      // Update the card in the current list
      setCards((prevCards) =>
        prevCards.map((card) =>
          card.id === cardId ? { ...card, ...updatedCard } : card
        )
      );

      // Update the selected card if it's the one being edited
      if (inspectorSelectedCard?.id === cardId) {
        setInspectorSelectedCard((prev) => ({ ...prev, ...updatedCard }));
      }

      if (currentBatchId) {
        void fetchBatchListableCount();
      }

      return updatedCard;
    } catch (error) {
      console.error(PQ_LOG, "[Inspector] Error saving card", error);
      throw error;
    }
  };

  return (
    <DashboardContainer>
      <style>
        {dragDropStyles}
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {fixedFilters && fixedFilters.batch__name && (
        <button
          onClick={handleBack}
          style={{
            marginBottom: "1rem",
            background: "none",
            border: "none",
            padding: "0.375rem 0",
            cursor: "pointer",
            outline: "none",
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
            color: "#1f2937",
            fontSize: "0.875rem",
            fontWeight: "500",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#3b82f6";
            e.currentTarget.style.gap = "0.5rem";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "#1f2937";
            e.currentTarget.style.gap = "0.375rem";
          }}
        >
          <ArrowLeft size={16} />
          Back
        </button>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap",
          marginBottom: "1.5rem",
        }}
      >
        <DashboardTitle style={{ margin: 0 }}>
          <DashboardTitleText>{batchName}</DashboardTitleText>
          {actualReadyCardsCount > 0 && (
            <DashboardCount>({actualReadyCardsCount} ready cards)</DashboardCount>
          )}
        </DashboardTitle>

        {currentBatchId && (
          <div style={{ flex: "1 1 320px", maxWidth: "560px" }}>
            <EbayBatchSyncBar batchId={currentBatchId} />
          </div>
        )}
      </div>

      <BatchOcrProgressPopup progress={batchOcrProgress} batchName={batchName} />

      <DashboardControls
        ordering={ordering}
        search={search}
        cardsPerPage={cardsPerPage}
        columnFilters={columnFilters}
        batchViewMode={batchViewMode}
        onOrderingChange={handleOrderingChange}
        onSearchSubmit={handleSearchSubmit}
        onSearchChange={handleSearchChange}
        onRefresh={handleRefresh}
        onBack={handleBack}
        onClearFilters={handleClearFilters}
        onStatusFilterChange={handleStatusFilterChange}
        onOcrStatusFilterChange={handleOcrStatusFilterChange}
        onCardsPerPageChange={handleCardsPerPageChange}
        onPersonalizeModalOpen={handlePersonalizeModalOpen}
        onBulkEditModalOpen={handleBulkEditModalOpen}
        onBulkReadyToList={handleBulkReadyToList}
        onBulkMarkAsDraft={handleBulkMarkAsDraft}
        onBulkDeleteModalOpen={openBulkDeleteModal}
        onAddCardModalOpen={openAddCardModal}
        onViewModeChange={handleViewModeChange}
        onListOnEbay={openListOnEbayModal}
        onTitleBuilderModalOpen={handleTitleBuilderModalOpen}
        isListingOnEbay={isListingOnEbay}
        selectedCardsCount={
          isAllPagesSelected
            ? (() => {
                // When all pages selected, calculate total non-listed cards
                // Use ratio of non-listed cards on current page to estimate total
                if (cards.length === 0) return 0;
                const nonListedRatio = nonListedCardsCount / cards.length;
                // Estimate total non-listed cards across all pages
                const nonListedTotal = Math.round(count * nonListedRatio);
                // Reduce by manually deselected cards
                return Math.max(
                  0,
                  nonListedTotal - deselectedCardIds.size
                );
              })()
            : selectedCards.size
        }
        totalCardsCount={count}
        readyCardsCount={actualReadyCardsCount}
        listOnEbayCount={
          currentBatchId
            ? batchListableCount ?? actualReadyCardsCount
            : actualReadyCardsCount
        }
        showListOnEbay={!!currentBatchId}
        notReadySelectedCardsCount={notReadySelectedCardsCount}
        readySelectedCardsCount={readySelectedCardsCount}
        isAllPagesSelected={isAllPagesSelected}
        showBackButton={false}
        showCardsPerPage={true}
        isSameAsGlobal={batchTitleBuilderConfig?.is_same_as_global}
        isEditModeEnabled={isEditModeEnabled}
        onEditModeChange={setIsEditModeEnabled}
        showTitleLengthFilter={showTitleLengthFilter}
      />

      {!isLoading && hasLongTitles && longTitleCount > 0 && (
        <motion.div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            padding: "0.75rem 1rem",
            borderRadius: "6px",
            marginBottom: "0.75rem",
            fontSize: "0.875rem",
            fontWeight: 500,
          }}
        >
          {getLongTitleBannerMessage(longTitleCount)}
          {columnFilters.status !== STATUS_FILTER_TITLE_LENGTH && (
            <>
              {" "}
              <button
                type="button"
                onClick={() =>
                  handleStatusFilterChange(STATUS_FILTER_TITLE_LENGTH)
                }
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  margin: 0,
                  color: "#991b1b",
                  fontWeight: 600,
                  fontSize: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                View cards
              </button>
            </>
          )}
        </motion.div>
      )}

      {isLoading ? (
        <DashboardStatus>Loading cards...</DashboardStatus>
      ) : cards.length === 0 ? (
        <DashboardError>No cards found</DashboardError>
      ) : batchViewMode === "inspector" ? (
        <div ref={cardInspectorRef}>
          <CardInspector
            cards={cards}
            selectedCard={inspectorSelectedCard}
            onCardSelect={setInspectorSelectedCard}
            setSelectedCard={setInspectorSelectedCard}
            onSaveCard={handleSaveCard}
            onImageClick={handleImageClick}
            batchTitleBuilderConfig={batchTitleBuilderConfig}
            onRefresh={refreshInspectorList}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            currentPage={currentPage}
            totalCount={count}
            cardsPerPage={cardsPerPage}
            nextPage={nextPage}
            prevPage={prevPage}
            onFetchPage={handleInspectorFetchPage}
          />
        </div>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeaders
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                  columnFilters={columnFilters}
                  onBulkEditModalOpen={handleBulkEditModalOpen}
                  onStatusFilterChange={handleStatusFilterChange}
                  onOcrStatusFilterChange={handleOcrStatusFilterChange}
                  showTitleLengthFilter={showTitleLengthFilter}
                  isAllSelected={isAllSelected}
                  onSelectAll={(checked) => {
                    if (checked) {
                      // Select all cards across all pages (excluding listed cards)
                      setIsAllPagesSelected(true);
                      setIsAllSelected(true);
                      setDeselectedCardIds(new Set()); // Clear deselected cards
                      // Select all non-listed cards on current page
                      const nonListedCardIds = cards
                        .filter((card) => !isCardLockedForEdit(card))
                        .map((card) => card.id);
                      setSelectedCards(new Set(nonListedCardIds));
                    } else {
                      // Deselect all cards
                      setSelectedCards(new Set());
                      setIsAllPagesSelected(false);
                      setIsAllSelected(false);
                      setDeselectedCardIds(new Set());
                    }
                  }}
                  cards={cards}
                  isEditModeEnabled={isEditModeEnabled}
                />
              </tr>
            </TableHead>
            <TableBody>
              {cards.length === 0 ? (
                <tr>
                  <NoDataCell
                    colSpan={
                      Object.values(columnVisibility).filter(Boolean).length + 2
                    }
                  >
                    No data
                  </NoDataCell>
                </tr>
              ) : (
                cards.map((card) => (
                  <TableRow
                    key={card.id}
                    card={card}
                    columnOrder={columnOrder}
                    columnVisibility={columnVisibility}
                    selectedCards={selectedCards}
                    imageLoading={imageLoading}
                    editingCell={editingCell}
                    editValue={editValue}
                    onCardSelect={handleCardSelect}
                    onCellClick={handleCellClick}
                    onImageClick={handleImageClick}
                    onImageLoadStart={handleImageLoadStart}
                    onImageLoad={handleImageLoad}
                    onImageError={handleImageError}
                    onStatusUpdate={handleStatusUpdate}
                    onRefreshCard={handleRefreshCard}
                    isRefreshingCard={refreshingCardIds.has(card.id)}
                    onRowClick={handleRowClick}
                    renderEditableCell={renderEditableCell}
                    batchTitleBuilderConfig={batchTitleBuilderConfig}
                    isEditModeEnabled={isEditModeEnabled}
                    isHighlighted={highlightedCardId === card.id}
                    rowRef={(el) => {
                      if (el) {
                        tableRowRefs.current[card.id] = el;
                      } else {
                        delete tableRowRefs.current[card.id];
                      }
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && cards.length > 0 && batchViewMode === "overview" && (
        <DashboardPagination>
          <PaginationArrow
            onClick={() => prevPage && fetchCards(prevPage, false)}
            disabled={!prevPage}
            className={!prevPage ? "disabled" : ""}
            aria-label="Previous page"
            title="Previous page"
          >
            <ChevronLeft size={15} />
          </PaginationArrow>

          {getPageNumbers(currentPage, Math.ceil(count / cardsPerPage)).map(
            (page, idx) =>
              typeof page === "number" ? (
                <PageButton
                  key={page}
                  $active={page === currentPage}
                  onClick={() =>
                    page !== currentPage &&
                    fetchCards(
                      buildBaseListUrl(API_BASE_URL, {
                        ordering,
                        search,
                        fixedFilters,
                        columnFilters,
                        batchViewMode,
                        cardsPerPage,
                        page,
                      }),
                      false
                    )
                  }
                >
                  {page}
                </PageButton>
              ) : (
                <PageEllipsis key={`${page}-${idx}`}>...</PageEllipsis>
              )
          )}

          <PaginationArrow
            onClick={() => nextPage && fetchCards(nextPage, false)}
            disabled={!nextPage}
            className={!nextPage ? "disabled" : ""}
            aria-label="Next page"
            title="Next page"
          >
            <ChevronRight size={15} />
          </PaginationArrow>
        </DashboardPagination>
      )}

      {/* Modals */}
      {isBulkEditModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "1.5rem",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Bulk Edit: {bulkEditData.columnName} ({selectedCards.size} card
                {selectedCards.size === 1 ? "" : "s"} selected)
              </h3>
              <button
                onClick={closeBulkEditModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "#6b7280",
                  cursor: "pointer",
                  padding: "0.25rem",
                }}
              >
                ×
              </button>
            </div>
            <div
              style={{
                padding: "1.5rem",
                flex: 1,
                overflowY: "auto",
              }}
            >
              <form
                onSubmit={handleBulkEditSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  <label
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#374151",
                    }}
                  >
                    New Value:
                  </label>
                  {bulkEditData.columnName === "status" ? (
                    <select
                      value={bulkEditData.newValue}
                      onChange={(e) =>
                        handleBulkEditChange("newValue", e.target.value)
                      }
                      style={{
                        padding: "0.75rem",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        color: "#1f2937",
                        background: "white",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                      }}
                    >
                      <option value="">Select status...</option>
                      <option value="draft">Draft</option>
                      <option value="ready">Ready to List</option>
                    </select>
                  ) : bulkEditData.columnName === "category" ||
                    bulkEditData.columnName === "ebay_category" ? (
                    <select
                      value={bulkEditData.newValue}
                      onChange={(e) =>
                        handleBulkEditChange("newValue", e.target.value)
                      }
                      style={{
                        padding: "0.75rem",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        color: "#1f2937",
                        background: "white",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                      }}
                    >
                      <option value="">Select Category</option>
                      <option value="Sport Trading Card">
                        Sport Trading Card
                      </option>
                      <option value="Non-Sport Trading Card">
                        Non-Sport Trading Card
                      </option>
                      <option value="CCG Individual Card">
                        CCG Individual Card
                      </option>
                      <option value="Collectible Sports Sticker Singles">
                        Collectible Sports Sticker Singles
                      </option>
                      <option value="Collectible Non-Sport Sticker Singles">
                        Collectible Non-Sport Sticker Singles
                      </option>
                    </select>
                  ) : bulkEditData.columnName === "condition" ||
                    bulkEditData.columnName === "ebay_condition" ? (
                    (() => {
                      // Get category from first selected card for bulk edit
                      const firstCardId = Array.from(selectedCards)[0];
                      const firstCard = cards.find((c) => c.id === firstCardId);
                      const category = firstCard
                        ? getFieldValue(firstCard, "category") ||
                          getFieldValue(firstCard, "ebay_category")
                        : null;
                      const conditionOptions = getConditionOptions(category);

                      return (
                        <select
                          value={bulkEditData.newValue}
                          onChange={(e) =>
                            handleBulkEditChange("newValue", e.target.value)
                          }
                          style={{
                            padding: "0.75rem",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "0.875rem",
                            color: "#1f2937",
                            background: "white",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select Condition</option>
                          {conditionOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      );
                    })()
                  ) : bulkEditData.columnName === "graded" ||
                    bulkEditData.columnName === "autograph" ||
                    bulkEditData.columnName === "ebay_graded" ||
                    bulkEditData.columnName === "ebay_vintage" ||
                    bulkEditData.columnName === "ebay_autographed" ? (
                    <select
                      value={bulkEditData.newValue}
                      onChange={(e) =>
                        handleBulkEditChange("newValue", e.target.value)
                      }
                      style={{
                        padding: "0.75rem",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        color: "#1f2937",
                        background: "white",
                        transition: "all 0.2s ease",
                        cursor: "pointer",
                      }}
                    >
                      <option value="">Select...</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  ) : bulkEditData.columnName === "available_quantity" ? (
                    <input
                      type="number"
                      value={bulkEditData.newValue}
                      onChange={(e) =>
                        handleBulkEditChange("newValue", e.target.value)
                      }
                      style={{
                        padding: "0.75rem",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        color: "#1f2937",
                        background: "white",
                        transition: "all 0.2s ease",
                      }}
                      placeholder="Enter number..."
                      min="0"
                    />
                  ) : bulkEditData.columnName === "sale_price" ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      value={bulkEditData.newValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*\.?\d*$/.test(val)) {
                          handleBulkEditChange("newValue", val);
                        }
                      }}
                      style={{
                        padding: "0.75rem",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        color: "#1f2937",
                        background: "white",
                        transition: "all 0.2s ease",
                      }}
                      placeholder="Enter price..."
                    />
                  ) : (
                    <input
                      type="text"
                      value={bulkEditData.newValue}
                      onChange={(e) =>
                        handleBulkEditChange("newValue", e.target.value)
                      }
                      style={{
                        padding: "0.75rem",
                        border: "2px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        color: "#1f2937",
                        background: "white",
                        transition: "all 0.2s ease",
                      }}
                      placeholder="Enter new value..."
                    />
                  )}
                </div>
                <div
                  style={{
                    padding: "1.5rem",
                    borderTop: "1px solid #e5e7eb",
                    display: "flex",
                    gap: "0.75rem",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeBulkEditModal}
                    style={{
                      padding: "0.75rem 1.5rem",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      background: "#6b7280",
                      color: "white",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!isEditModeEnabled}
                    style={{
                      padding: "0.75rem 1.5rem",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      cursor: isEditModeEnabled ? "pointer" : "not-allowed",
                      transition: "all 0.2s ease",
                      background: isEditModeEnabled ? "#3b82f6" : "#9ca3af",
                      color: "white",
                      opacity: isEditModeEnabled ? 1 : 0.6,
                    }}
                  >
                    Update Cards
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {isBulkDeleteModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              width: "100%",
              maxWidth: "400px",
              padding: "1.5rem",
            }}
          >
            <h3
              style={{
                margin: "0 0 1rem 0",
                fontSize: "1.25rem",
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Delete Cards
            </h3>
            <p
              style={{
                margin: "0 0 1.5rem 0",
                color: "#6b7280",
              }}
            >
              Are you sure you want to delete {selectedCards.size} card
              {selectedCards.size === 1 ? "" : "s"}? This action cannot be
              undone.
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={closeBulkDeleteModal}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: "#6b7280",
                  color: "white",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: "#ef4444",
                  color: "white",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {isListOnEbayModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              width: "100%",
              maxWidth: "400px",
              padding: "1.5rem",
            }}
          >
            <h3
              style={{
                margin: "0 0 1rem 0",
                fontSize: "1.25rem",
                fontWeight: 700,
                color: "#111827",
              }}
            >
              List/Resync Cards on eBay
            </h3>
            <p
              style={{
                margin: "0 0 1.5rem 0",
                color: "#6b7280",
              }}
            >
              {currentBatchId && batchSyncPreview
                ? `This will list or resync ${batchSyncPreview.to_process} card${
                    batchSyncPreview.to_process === 1 ? "" : "s"
                  }${hasActiveCardFilters ? " matching the current filters" : ""} on eBay. The sync runs in the background — you'll be taken to the task page to track progress.`
                : `Are you sure you want to list or resync ${actualReadyCardsCount} ready card${
                    actualReadyCardsCount === 1 ? "" : "s"
                  } on eBay? The sync runs in the background — you'll be taken to the task page to track progress.`}
            </p>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={closeListOnEbayModal}
                disabled={isListingOnEbay}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: isListingOnEbay ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  background: "#6b7280",
                  color: "white",
                  opacity: isListingOnEbay ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleListOnEbay}
                disabled={isListingOnEbay}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: isListingOnEbay ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  background: "#3b82f6",
                  color: "white",
                  opacity: isListingOnEbay ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {isListingOnEbay && (
                  <div
                    style={{
                      width: "14px",
                      height: "14px",
                      border: "2px solid rgba(255, 255, 255, 0.3)",
                      borderTop: "2px solid white",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                )}
                {isListingOnEbay ? "Syncing..." : "List/Resync on eBay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay when listing on eBay */}
      {isListingOnEbay && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "2rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "4px solid rgba(59, 130, 246, 0.2)",
                borderTop: "4px solid #3b82f6",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "1rem",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Listing cards on eBay...
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "0.875rem",
                color: "#6b7280",
                textAlign: "center",
              }}
            >
              Starting background sync...
            </p>
          </div>
        </div>
      )}

      {isQuickAddModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "1.5rem",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Quick Add Card
              </h3>
              <button
                onClick={closeQuickAddModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "#6b7280",
                  cursor: "pointer",
                  padding: "0.25rem",
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                padding: "1.5rem",
                flex: 1,
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                {/* File Upload */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Choose Files (Multiple)
                  </label>
                  <QuickAddFileInput
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleQuickAddFileSelect}
                  />
                </div>

                {/* Presets Form - Removed, using modal instead */}
                {false && (
                  <div
                    style={{
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "1.5rem",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0 0 1rem 0",
                        fontSize: "1.25rem",
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      Default Metadata Presets
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "1rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#374151",
                            marginBottom: "0.5rem",
                          }}
                        >
                          Category
                        </label>
                        <input
                          type="text"
                          value={quickAddData.presetsDraft.Category}
                          onChange={(e) =>
                            handleQuickAddPresetChange(
                              "Category",
                              e.target.value
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "1rem",
                            color: "#1f2937",
                            background: "white",
                            transition: "all 0.2s ease",
                          }}
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#374151",
                            marginBottom: "0.5rem",
                          }}
                        >
                          Suggested Price
                        </label>
                        <input
                          type="text"
                          value={quickAddData.presetsDraft.SuggestedPrice}
                          onChange={(e) =>
                            handleQuickAddPresetChange(
                              "SuggestedPrice",
                              e.target.value
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "1rem",
                            color: "#1f2937",
                            background: "white",
                            transition: "all 0.2s ease",
                          }}
                        />
                      </div>
                    </div>
                    <div
                      style={{
                        marginBottom: "1rem",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "#374151",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Condition
                      </label>
                      <input
                        type="text"
                        value={quickAddData.presetsDraft.Condition}
                        onChange={(e) =>
                          handleQuickAddPresetChange(
                            "Condition",
                            e.target.value
                          )
                        }
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: "2px solid #e5e7eb",
                          borderRadius: "8px",
                          fontSize: "1rem",
                          color: "#1f2937",
                          background: "white",
                          transition: "all 0.2s ease",
                        }}
                      />
                    </div>

                    <h5
                      style={{
                        margin: "1.5rem 0 1rem 0",
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#374151",
                        paddingBottom: "0.5rem",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Metadata
                    </h5>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "1rem",
                        marginBottom: "1rem",
                      }}
                    >
                      {Object.keys(quickAddData.presetsDraft.Metadata).map(
                        (key) => (
                          <div key={key}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                color: "#374151",
                                marginBottom: "0.5rem",
                              }}
                            >
                              {key}
                            </label>
                            <input
                              type="text"
                              value={quickAddData.presetsDraft.Metadata[key]}
                              onChange={(e) =>
                                handleQuickAddPresetChange(
                                  `Metadata.${key}`,
                                  e.target.value
                                )
                              }
                              style={{
                                width: "100%",
                                padding: "0.75rem",
                                border: "2px solid #e5e7eb",
                                borderRadius: "8px",
                                fontSize: "1rem",
                                color: "#1f2937",
                                background: "white",
                                transition: "all 0.2s ease",
                              }}
                            />
                          </div>
                        )
                      )}
                    </div>

                    <h5
                      style={{
                        margin: "1.5rem 0 1rem 0",
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#374151",
                        paddingBottom: "0.5rem",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      eBay Listing
                    </h5>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "1rem",
                        marginBottom: "1rem",
                      }}
                    >
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#374151",
                            marginBottom: "0.5rem",
                          }}
                        >
                          Category
                        </label>
                        <input
                          type="text"
                          value={quickAddData.presetsDraft.eBayListing.Category}
                          onChange={(e) =>
                            handleQuickAddPresetChange(
                              "eBayListing.Category",
                              e.target.value
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "1rem",
                            color: "#1f2937",
                            background: "white",
                            transition: "all 0.2s ease",
                          }}
                        />
                      </div>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            color: "#374151",
                            marginBottom: "0.5rem",
                          }}
                        >
                          Condition
                        </label>
                        <input
                          type="text"
                          value={
                            quickAddData.presetsDraft.eBayListing.Condition
                          }
                          onChange={(e) =>
                            handleQuickAddPresetChange(
                              "eBayListing.Condition",
                              e.target.value
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "0.75rem",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "1rem",
                            color: "#1f2937",
                            background: "white",
                            transition: "all 0.2s ease",
                          }}
                        />
                      </div>
                    </div>

                    <h5
                      style={{
                        margin: "1.5rem 0 1rem 0",
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#374151",
                        paddingBottom: "0.5rem",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      eBay Item Specifics
                    </h5>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "1rem",
                        marginBottom: "1rem",
                      }}
                    >
                      {Object.keys(
                        quickAddData.presetsDraft.eBayListing.ItemSpecifics
                      ).map((key) => (
                        <div key={key}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "#374151",
                              marginBottom: "0.5rem",
                            }}
                          >
                            {key}
                          </label>
                          <input
                            type="text"
                            value={
                              quickAddData.presetsDraft.eBayListing
                                .ItemSpecifics[key]
                            }
                            onChange={(e) =>
                              handleQuickAddPresetChange(
                                `eBayListing.ItemSpecifics.${key}`,
                                e.target.value
                              )
                            }
                            style={{
                              width: "100%",
                              padding: "0.75rem",
                              border: "2px solid #e5e7eb",
                              borderRadius: "8px",
                              fontSize: "1rem",
                              color: "#1f2937",
                              background: "white",
                              transition: "all 0.2s ease",
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        marginTop: "1rem",
                      }}
                    >
                      <button
                        onClick={handleQuickAddApplyPresets}
                        style={{
                          padding: "0.75rem 1.5rem",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          background: "#3b82f6",
                          color: "white",
                        }}
                      >
                        Apply Presets
                      </button>
                      <button
                        onClick={handleQuickAddCancelPresets}
                        style={{
                          padding: "0.75rem 1.5rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "8px",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          background: "#f3f4f6",
                          color: "#374151",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* File Preview */}
                {quickAddData.selectedFiles.length > 0 && (
                  <QuickAddImagePreviewSection>
                    <QuickAddImagePreviewTitle>
                      Selected Images ({quickAddData.selectedFiles.length})
                    </QuickAddImagePreviewTitle>
                    <QuickAddImagePreviewGrid>
                      {Array.from(
                        {
                          length: Math.ceil(
                            quickAddData.selectedFiles.length / 2
                          ),
                        },
                        (_, pairIndex) => {
                          const startIndex = pairIndex * 2;
                          const pairFiles = quickAddData.selectedFiles.slice(
                            startIndex,
                            startIndex + 2
                          );

                          return (
                            <QuickAddCardPairContainer key={pairIndex}>
                              {pairFiles.map((file, fileIndex) => {
                                const index = startIndex + fileIndex;
                                return (
                                  <QuickAddImagePreviewItem key={index}>
                                    <QuickAddImagePreview
                                      src={URL.createObjectURL(file)}
                                      alt={`Preview ${index + 1}`}
                                    />
                                    <QuickAddHoverOverlay className="hover-overlay">
                                      <QuickAddActionButton
                                        className="view"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleQuickAddViewImage(file);
                                        }}
                                        disabled={quickAddData.isUploading}
                                        title="View full size"
                                      >
                                        <HiOutlineViewfinderCircle />
                                      </QuickAddActionButton>
                                      <QuickAddActionButton
                                        className="delete"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleQuickAddDeleteImage(index);
                                        }}
                                        disabled={quickAddData.isUploading}
                                        title="Delete image"
                                      >
                                        <FiTrash />
                                      </QuickAddActionButton>
                                    </QuickAddHoverOverlay>
                                    <QuickAddImageDetails>
                                      <QuickAddImageName title={file.name}>
                                        {file.name}
                                      </QuickAddImageName>
                                      <QuickAddImageSize>
                                        {(file.size / 1024 / 1024).toFixed(2)}{" "}
                                        MB
                                      </QuickAddImageSize>
                                      {quickAddData.uploadProgress[
                                        file.name
                                      ] && (
                                        <QuickAddProgressContainer>
                                          <div
                                            style={{
                                              fontSize: 12,
                                              color: "#6b7280",
                                              marginBottom: 4,
                                            }}
                                          >
                                            {Math.round(
                                              (quickAddData.uploadProgress[
                                                file.name
                                              ].uploaded /
                                                quickAddData.uploadProgress[
                                                  file.name
                                                ].total) *
                                                100
                                            )}
                                            %
                                          </div>
                                          <QuickAddProgressBarBackground>
                                            <QuickAddProgressBarFill
                                              width={
                                                (quickAddData.uploadProgress[
                                                  file.name
                                                ].uploaded /
                                                  quickAddData.uploadProgress[
                                                    file.name
                                                  ].total) *
                                                100
                                              }
                                            />
                                          </QuickAddProgressBarBackground>
                                        </QuickAddProgressContainer>
                                      )}
                                    </QuickAddImageDetails>
                                  </QuickAddImagePreviewItem>
                                );
                              })}
                            </QuickAddCardPairContainer>
                          );
                        }
                      )}
                    </QuickAddImagePreviewGrid>
                  </QuickAddImagePreviewSection>
                )}

                {/* Error Message */}
                {quickAddData.error && (
                  <div
                    style={{
                      color: "#ef4444",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      padding: "0.75rem",
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                    }}
                  >
                    {quickAddData.error}
                  </div>
                )}

                {/* Success Message */}
                {quickAddData.uploadStatus && (
                  <div
                    style={{
                      color: "#059669",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      padding: "0.75rem",
                      background: "#f0fdf4",
                      border: "1px solid #bbf7d0",
                      borderRadius: "6px",
                    }}
                  >
                    {quickAddData.uploadStatus}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Action Buttons */}
            <div
              ref={quickAddActionsRef}
              style={{
                padding: "1.5rem",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={closeQuickAddModal}
                disabled={quickAddData.isUploading}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: quickAddData.isUploading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  background: "#6b7280",
                  color: "white",
                  opacity: quickAddData.isUploading ? 0.6 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleQuickAddUpload}
                disabled={
                  quickAddData.selectedFiles.length === 0 ||
                  quickAddData.isUploading
                }
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor:
                    quickAddData.selectedFiles.length === 0 ||
                    quickAddData.isUploading
                      ? "not-allowed"
                      : "pointer",
                  transition: "all 0.2s ease",
                  background: "#3b82f6",
                  color: "white",
                  opacity:
                    quickAddData.selectedFiles.length === 0 ||
                    quickAddData.isUploading
                      ? 0.6
                      : 1,
                }}
              >
                {quickAddData.isUploading
                  ? "Uploading..."
                  : `Upload ${quickAddData.selectedFiles.length} File(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Magnified View Modal */}
      {quickAddMagnifiedImage && (
        <QuickAddMagnifiedViewOverlay onClick={closeQuickAddMagnifiedView}>
          <QuickAddMagnifiedViewContainer onClick={(e) => e.stopPropagation()}>
            <QuickAddMagnifiedImage
              src={quickAddMagnifiedImage}
              alt="Magnified view"
            />
            <QuickAddCloseButton onClick={closeQuickAddMagnifiedView}>
              ×
            </QuickAddCloseButton>
          </QuickAddMagnifiedViewContainer>
        </QuickAddMagnifiedViewOverlay>
      )}

      {/* Quick Add Preset Selection Modal */}
      {isQuickAddPresetModalOpen && (
        <QuickAddModalOverlay onClick={handleQuickAddClosePresetModal}>
          <QuickAddPresetModalContainer onClick={(e) => e.stopPropagation()}>
            <QuickAddPresetModalHeader>
              <h3
                style={{
                  margin: 0,
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: "#111827",
                }}
              >
                Select Preset
              </h3>
              <QuickAddPresetModalCloseButton
                onClick={handleQuickAddClosePresetModal}
                title="Close"
              >
                <X size={18} />
              </QuickAddPresetModalCloseButton>
            </QuickAddPresetModalHeader>
            <QuickAddPresetModalSearchSection>
              <QuickAddPresetModalSearch>
                <QuickAddSearchIconWrapper>
                  <Search size={16} />
                </QuickAddSearchIconWrapper>
                <QuickAddPresetModalSearchInput
                  type="text"
                  placeholder="Search presets..."
                  value={quickAddPresetSearchQuery}
                  onChange={(e) => setQuickAddPresetSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                {quickAddPresetSearchQuery && (
                  <QuickAddPresetCount>
                    {quickAddFilteredPresets.length}{" "}
                    {quickAddFilteredPresets.length === 1
                      ? "result"
                      : "results"}
                  </QuickAddPresetCount>
                )}
              </QuickAddPresetModalSearch>
            </QuickAddPresetModalSearchSection>
            <QuickAddPresetModalContent>
              <QuickAddPresetModalList>
                {quickAddFilteredPresets.length === 0 ? (
                  <QuickAddPresetModalEmptyState>
                    {quickAddPresetSearchQuery
                      ? "No presets found matching your search"
                      : "No presets available"}
                  </QuickAddPresetModalEmptyState>
                ) : (
                  quickAddFilteredPresets.map((preset) => (
                    <QuickAddPresetModalItem
                      key={preset.id}
                      onClick={() => handleQuickAddSelectPreset(preset)}
                      className={
                        quickAddSelectedPreset?.id === preset.id
                          ? "selected"
                          : ""
                      }
                    >
                      {preset.name || "Unnamed Preset"}
                    </QuickAddPresetModalItem>
                  ))
                )}
              </QuickAddPresetModalList>
            </QuickAddPresetModalContent>
            <QuickAddPresetModalFooter>
              <button
                onClick={handleQuickAddClosePresetModal}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: "#f3f4f6",
                  color: "#374151",
                }}
              >
                Cancel
              </button>
            </QuickAddPresetModalFooter>
          </QuickAddPresetModalContainer>
        </QuickAddModalOverlay>
      )}

      {/* Quick Add Batch Specific Preset Modal */}
      {isQuickAddBatchSpecificPresetModalOpen && (
        <QuickAddModalOverlay onClick={handleQuickAddCloseBatchSpecificPresetModal}>
          <QuickAddBatchSpecificPresetModalContainer
            onClick={(e) => e.stopPropagation()}
          >
            <QuickAddBatchSpecificPresetModalHeader>
              <h3
                style={{
                  margin: 0,
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  color: "#111827",
                }}
              >
                Preset Details
              </h3>
              <QuickAddPresetModalCloseButton
                onClick={handleQuickAddCloseBatchSpecificPresetModal}
                title="Close"
              >
                <X size={18} />
              </QuickAddPresetModalCloseButton>
            </QuickAddBatchSpecificPresetModalHeader>
            <QuickAddBatchSpecificPresetModalContent>
              <div>
                <QuickAddPresetsSubtitle>eBay Item Specifics</QuickAddPresetsSubtitle>
                <QuickAddGrid2>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Category</QuickAddLabel>
                    <QuickAddSelect
                      value={quickAddBatchSpecificPresetFormData.category}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "category",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Sport Trading Card">
                        Sport Trading Card
                      </option>
                      <option value="Non-Sport Trading Card">
                        Non-Sport Trading Card
                      </option>
                      <option value="CCG Individual Card">
                        CCG Individual Card
                      </option>
                      <option value="Collectible Sports Sticker Singles">
                        Collectible Sports Sticker Singles
                      </option>
                      <option value="Collectible Non-Sport Sticker Singles">
                        Collectible Non-Sport Sticker Singles
                      </option>
                    </QuickAddSelect>
                  </QuickAddInputGroup>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Manufacturer</QuickAddLabel>
                    <QuickAddInput
                      type="text"
                      value={quickAddBatchSpecificPresetFormData.manufacturer}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "manufacturer",
                          e.target.value
                        )
                      }
                      placeholder="Enter manufacturer"
                    />
                  </QuickAddInputGroup>
                  {CATEGORY_SPECIFIC_FIELD_LABEL[quickAddBatchSpecificPresetFormData.category] && (
                    <QuickAddInputGroup>
                      <QuickAddLabel>
                        {CATEGORY_SPECIFIC_FIELD_LABEL[quickAddBatchSpecificPresetFormData.category]}
                      </QuickAddLabel>
                      <QuickAddInput
                        type="text"
                        value={quickAddBatchSpecificPresetFormData.sportGameFranchise}
                        onChange={(e) =>
                          handleQuickAddBatchSpecificPresetInputChange(
                            "sportGameFranchise",
                            e.target.value
                          )
                        }
                        placeholder={`Enter ${CATEGORY_SPECIFIC_FIELD_LABEL[
                          quickAddBatchSpecificPresetFormData.category
                        ].toLowerCase()}`}
                      />
                    </QuickAddInputGroup>
                  )}
                  <QuickAddInputGroup>
                    <QuickAddLabel>Autographed</QuickAddLabel>
                    <QuickAddSelect
                      value={quickAddBatchSpecificPresetFormData.autographed}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "autographed",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </QuickAddSelect>
                  </QuickAddInputGroup>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Graded</QuickAddLabel>
                    <QuickAddSelect
                      value={quickAddBatchSpecificPresetFormData.graded}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "graded",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </QuickAddSelect>
                  </QuickAddInputGroup>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Condition</QuickAddLabel>
                    <QuickAddSelect
                      value={quickAddBatchSpecificPresetFormData.condition}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "condition",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Condition</option>
                      {getConditionOptions(
                        quickAddBatchSpecificPresetFormData.category
                      ).map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </QuickAddSelect>
                  </QuickAddInputGroup>
                </QuickAddGrid2>
                <QuickAddGrid2>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Language</QuickAddLabel>
                    <QuickAddInput
                      type="text"
                      value={quickAddBatchSpecificPresetFormData.language}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "language",
                          e.target.value
                        )
                      }
                      placeholder="Enter language"
                    />
                  </QuickAddInputGroup>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Set</QuickAddLabel>
                    <QuickAddInput
                      type="text"
                      value={quickAddBatchSpecificPresetFormData.set}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "set",
                          e.target.value
                        )
                      }
                      placeholder="Enter set"
                    />
                  </QuickAddInputGroup>
                </QuickAddGrid2>
                <QuickAddGrid2>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Year Manufactured</QuickAddLabel>
                    <QuickAddInput
                      type="text"
                      value={quickAddBatchSpecificPresetFormData.yearManufactured}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "yearManufactured",
                          e.target.value
                        )
                      }
                      placeholder="Enter year manufactured"
                    />
                  </QuickAddInputGroup>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Vintage</QuickAddLabel>
                    <QuickAddSelect
                      value={quickAddBatchSpecificPresetFormData.vintage}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "vintage",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </QuickAddSelect>
                  </QuickAddInputGroup>
                </QuickAddGrid2>
                <QuickAddGrid2>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Condition Description</QuickAddLabel>
                    <QuickAddInput
                      type="text"
                      value={quickAddBatchSpecificPresetFormData.conditionDescription}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "conditionDescription",
                          e.target.value
                        )
                      }
                      placeholder="Enter condition description"
                    />
                  </QuickAddInputGroup>
                  <QuickAddInputGroup>
                    <QuickAddLabel>Country of Origin</QuickAddLabel>
                    <QuickAddInput
                      type="text"
                      value={quickAddBatchSpecificPresetFormData.country}
                      onChange={(e) =>
                        handleQuickAddBatchSpecificPresetInputChange(
                          "country",
                          e.target.value
                        )
                      }
                      placeholder="Enter country"
                    />
                  </QuickAddInputGroup>
                </QuickAddGrid2>
              </div>
            </QuickAddBatchSpecificPresetModalContent>
            <QuickAddBatchSpecificPresetModalFooter>
              <QuickAddButton
                className="secondary"
                onClick={handleQuickAddCloseBatchSpecificPresetModal}
              >
                Cancel
              </QuickAddButton>
              <QuickAddButton
                className="primary"
                onClick={handleQuickAddSaveBatchSpecificPreset}
              >
                Save Preset
              </QuickAddButton>
            </QuickAddBatchSpecificPresetModalFooter>
          </QuickAddBatchSpecificPresetModalContainer>
        </QuickAddModalOverlay>
      )}

      {/* Title Builder Modal */}
      {isTitleBuilderOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "1rem",
            backdropFilter: "blur(4px)",
          }}
          onClick={handleCloseTitleBuilder}
          onDragOver={handlePreventDropOutside}
          onDrop={handlePreventDropOutside}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "2rem",
              maxWidth: "900px",
              width: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
            onDragOver={handlePreventDropOutside}
            onDrop={handlePreventDropOutside}
          >
            <h3
              style={{
                margin: "0 0 1.5rem 0",
                fontSize: "1.5rem",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Title Builder
            </h3>
            <TitleBuilderTwoColumnLayout
              onDragOver={handlePreventDropOutside}
              onDrop={handlePreventDropOutside}
            >
              {/* Title Builder Column */}
              <TitleBuilderColumn
                onDragOver={handlePreventDropOutside}
                onDrop={handlePreventDropOutside}
              >
                <TitleBuilderColumnTitle>
                  Title Builder (Drag fields here)
                </TitleBuilderColumnTitle>
                <TitleBuilderDropZone
                  ref={titleBuilderDropZoneRef}
                  onDragOver={(e) => {
                    handleTitleBuilderDragOver(e);
                    setIsDraggingOverDropZone(true);
                  }}
                  onDragLeave={() => setIsDraggingOverDropZone(false)}
                  onDrop={handleTitleBuilderDropToBuilder}
                  $isDragging={isDraggingOverDropZone}
                >
                  {!quickAddData.titleBuilder ||
                  quickAddData.titleBuilder.length === 0 ? (
                    <TitleBuilderEmptyState>
                      Drag fields from the right column to build your title
                    </TitleBuilderEmptyState>
                  ) : (
                    quickAddData.titleBuilder.map((item, index) => {
                      const fieldName =
                        typeof item === "string" ? item : item.field;
                      const prefix =
                        typeof item === "string" ? "" : item.prefix || "";
                      const suffix =
                        typeof item === "string" ? "" : item.suffix || "";
                      return (
                        <TitleBuilderFieldItem
                          key={`${fieldName}-${index}`}
                          onDragOver={(e) =>
                            handleTitleBuilderDragOver(e, index)
                          }
                          onDragLeave={handleTitleBuilderDragLeave}
                          onDrop={(e) => handleTitleBuilderDrop(e, index)}
                          className={`in-builder ${
                            dragOverIndex === index ? "drag-over" : ""
                          } ${draggedIndex === index ? "dragging" : ""}`}
                          $isInBuilder
                        >
                          <TitleBuilderFieldContent>
                            {editingFieldIndex === index ? (
                              <TitleBuilderFieldInputWrapper
                                className="field-input-wrapper"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <TitleBuilderFieldInput
                                  value={prefix}
                                  data-field-index={index}
                                  data-input-type="prefix"
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    // Auto-resize textarea
                                    e.target.style.height = "auto";
                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                    handleTitleBuilderPrefixChange(
                                      index,
                                      newValue
                                    );
                                  }}
                                  onBlur={handleTitleBuilderInputBlur}
                                  placeholder="Prefix"
                                  rows={1}
                                />
                                <TitleBuilderFieldNamePrefix>
                                  {fieldName}
                                </TitleBuilderFieldNamePrefix>
                                <TitleBuilderFieldInput
                                  value={suffix}
                                  data-field-index={index}
                                  data-input-type="suffix"
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    // Auto-resize textarea
                                    e.target.style.height = "auto";
                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                    handleTitleBuilderSuffixChange(
                                      index,
                                      newValue
                                    );
                                  }}
                                  onBlur={handleTitleBuilderInputBlur}
                                  placeholder="Suffix"
                                  autoFocus
                                  rows={1}
                                />
                              </TitleBuilderFieldInputWrapper>
                            ) : (
                              <TitleBuilderDragHandle
                                draggable
                                onDragStart={(e) =>
                                  handleTitleBuilderDragStart(
                                    e,
                                    fieldName,
                                    index
                                  )
                                }
                                onDragEnd={handleTitleBuilderDragEnd}
                              >
                                <TitleBuilderFieldName
                                  $isDraggable
                                  type="text"
                                  value={
                                    (prefix ? prefix + " " : "") +
                                    fieldName +
                                    (suffix ? " " + suffix : "")
                                  }
                                  readOnly
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTitleBuilderFieldNameClick(index, e);
                                  }}
                                  onFocus={(e) => {
                                    e.target.blur();
                                    handleTitleBuilderFieldNameClick(index, e);
                                  }}
                                  onMouseDown={(e) => {
                                    // Allow dragging
                                    if (e.target === e.currentTarget) {
                                      // Only prevent if clicking on the input itself
                                    }
                                  }}
                                />
                              </TitleBuilderDragHandle>
                            )}
                          </TitleBuilderFieldContent>
                          <TitleBuilderRemoveButton
                            onClick={() => handleTitleBuilderRemoveField(index)}
                            title="Remove field"
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            ×
                          </TitleBuilderRemoveButton>
                        </TitleBuilderFieldItem>
                      );
                    })
                  )}
                </TitleBuilderDropZone>
              </TitleBuilderColumn>

              {/* Available Fields Column */}
              <TitleBuilderColumn
                onDragOver={handlePreventDropOutside}
                onDrop={handlePreventDropOutside}
              >
                <TitleBuilderColumnTitle>
                  Available Fields
                </TitleBuilderColumnTitle>
                <TitleBuilderAvailableFieldsList
                  onDragOver={handlePreventDropOutside}
                  onDrop={handlePreventDropOutside}
                >
                  {availableFields
                    .filter((field) => {
                      const getFieldName = (item) =>
                        typeof item === "string" ? item : item.field;
                      const titleBuilder = quickAddData.titleBuilder || [];
                      const existsAsField = titleBuilder.some(
                        (item) => getFieldName(item) === field
                      );
                      return !existsAsField;
                    })
                    .map((field) => (
                      <TitleBuilderFieldItem
                        key={field}
                        draggable
                        onDragStart={(e) =>
                          handleTitleBuilderDragStart(e, field)
                        }
                        onDragEnd={handleTitleBuilderDragEnd}
                        className={draggedField === field ? "dragging" : ""}
                      >
                        <TitleBuilderAvailableFieldName
                          type="text"
                          value={field}
                          readOnly
                        />
                      </TitleBuilderFieldItem>
                    ))}
                </TitleBuilderAvailableFieldsList>
              </TitleBuilderColumn>
            </TitleBuilderTwoColumnLayout>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                marginTop: "2rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleCloseTitleBuilder}
                style={{
                  padding: "0.875rem 2rem",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: "#3b82f6",
                  color: "white",
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {isPersonalizeTableModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              width: "100%",
              maxWidth: "700px",
              maxHeight: "80vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "1.5rem",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Personalize Table
              </h3>
              <button
                onClick={handlePersonalizeModalClose}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "#6b7280",
                  cursor: "pointer",
                  padding: "0.25rem",
                }}
              >
                ×
              </button>
            </div>

            {/* Tab Navigation */}
            <div
              style={{
                display: "flex",
                borderBottom: "1px solid #e5e7eb",
                marginBottom: "1.5rem",
                paddingLeft: "1.5rem",
                paddingRight: "1.5rem",
              }}
            >
              <button
                onClick={() => setActivePersonaliseTab("showHide")}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  background: "none",
                  color:
                    activePersonaliseTab === "showHide" ? "#2563eb" : "#6b7280",
                  fontSize: "0.875rem",
                  fontWeight:
                    activePersonaliseTab === "showHide" ? "600" : "500",
                  cursor: "pointer",
                  borderBottom: `2px solid ${
                    activePersonaliseTab === "showHide"
                      ? "#2563eb"
                      : "transparent"
                  }`,
                  outline: "none",
                  boxShadow: "none",
                  borderRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                Hide/Show Columns
              </button>
              <button
                onClick={() => setActivePersonaliseTab("reorder")}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  background: "none",
                  color:
                    activePersonaliseTab === "reorder" ? "#2563eb" : "#6b7280",
                  fontSize: "0.875rem",
                  fontWeight:
                    activePersonaliseTab === "reorder" ? "600" : "500",
                  cursor: "pointer",
                  borderBottom: `2px solid ${
                    activePersonaliseTab === "reorder"
                      ? "#2563eb"
                      : "transparent"
                  }`,
                  outline: "none",
                  boxShadow: "none",
                  borderRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              >
                Reorder Columns
              </button>
            </div>

            <div
              style={{
                padding: "0 1.5rem",
                flex: 1,
                overflowY: "auto",
              }}
            >
              {activePersonaliseTab === "showHide" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "0.75rem",
                      marginBottom: "1rem",
                      paddingBottom: "1rem",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <button
                      onClick={() => handleSelectAllColumns()}
                      style={{
                        padding: "0.5rem 1rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        background: "white",
                        color: "#374151",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => handleDeselectAllColumns()}
                      style={{
                        padding: "0.5rem 1rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        background: "white",
                        color: "#374151",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      Deselect All
                    </button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                      maxHeight: "400px",
                      overflowY: "auto",
                    }}
                  >
                    {Object.keys(columnVisibility).map((columnKey) => {
                      const isChecked = columnVisibility[columnKey];
                      return (
                        <label
                          key={columnKey}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.75rem",
                            padding: "0.375rem 0.5rem",
                            borderRadius: "6px",
                            cursor: "pointer",
                            transition: "background-color 0.15s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f9fafb";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          <div
                            style={{
                              position: "relative",
                              width: "18px",
                              height: "18px",
                              minWidth: "18px",
                              border: `2px solid ${isChecked ? "#6366f1" : "#d1d5db"}`,
                              borderRadius: "4px",
                              backgroundColor: isChecked ? "#6366f1" : "white",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            onClick={() => handleColumnVisibilityChange(columnKey)}
                          >
                            {isChecked && (
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{
                                  pointerEvents: "none",
                                }}
                              >
                                <path
                                  d="M10 3L4.5 8.5L2 6"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleColumnVisibilityChange(columnKey)}
                            style={{
                              position: "absolute",
                              opacity: 0,
                              width: 0,
                              height: 0,
                              pointerEvents: "none",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "0.8125rem",
                              color: "#374151",
                              fontWeight: 500,
                              userSelect: "none",
                            }}
                          >
                            {getColumnDisplayName(columnKey)}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}

              {activePersonaliseTab === "reorder" && (
                <div
                  style={{
                    minHeight: "300px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      color: "#6b7280",
                      fontSize: "0.8125rem",
                    }}
                  >
                    Drag and drop columns to reorder them. The order will be
                    applied to the table.
                  </p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={columnOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                          maxHeight: "400px",
                          overflowY: "auto",
                        }}
                      >
                        {columnOrder.map((columnKey, index) => (
                          <SortableItem
                            key={columnKey}
                            id={columnKey}
                            index={index}
                            getColumnDisplayName={getColumnDisplayName}
                            activeId={activeId}
                          />
                        ))}
                      </div>
                    </SortableContext>
                    <DragOverlay>
                      {activeId ? (
                        <SortableItem
                          id={activeId}
                          index={columnOrder.indexOf(activeId)}
                          getColumnDisplayName={getColumnDisplayName}
                          activeId={activeId}
                        />
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                </div>
              )}
            </div>

            {/* Bottom Action Buttons */}
            <div
              style={{
                padding: "1.5rem",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                gap: "0.75rem",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  // Reset to default visibility
                  // Clear localStorage and reset to defaults
                  localStorage.removeItem("dashboard_columnOrder");
                  localStorage.removeItem("dashboard_columnVisibility");
                  setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
                  setColumnOrder(DEFAULT_COLUMN_ORDER);
                }}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: "white",
                  color: "#374151",
                }}
              >
                Reset
              </button>
              <button
                onClick={handlePersonalizeModalClose}
                style={{
                  padding: "0.75rem 1.5rem",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: "#3b82f6",
                  color: "white",
                }}
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal - Shows both front and back images in full size */}
      {selectedCardForModal && (
        <MagnifiedViewOverlay onClick={handleCloseImageModal}>
          <MagnifiedViewContainer onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseImageModal}>×</CloseButton>
            <MagnifiedImagesRow>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <ImageLabel>Front</ImageLabel>
                <DualMagnifiedImage
                  src={selectedCardForModal.front_image}
                  alt={`Card ${selectedCardForModal.id} front`}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <ImageLabel>Back</ImageLabel>
                <DualMagnifiedImage
                  src={selectedCardForModal.back_image}
                  alt={`Card ${selectedCardForModal.id} back`}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </MagnifiedImagesRow>
          </MagnifiedViewContainer>
        </MagnifiedViewOverlay>
      )}

      {/* Title Builder Modal */}
      <TitleBuilder
        isOpen={isTitleBuilderModalOpen}
        onClose={() => setIsTitleBuilderModalOpen(false)}
        availableFields={titleBuilderAvailableFields}
        titleBuilderType={titleBuilderType}
        onTypeChange={handleTitleBuilderTypeChange}
        currentBatchId={currentBatchId}
        apiBaseUrl={API_BASE_URL}
        refreshAccessToken={refreshAccessToken}
        onSave={async (configuration) => {
          // Refresh the batch title builder config if batch-specific or global (since global also hits batch endpoint)
          if (currentBatchId) {
            await fetchBatchTitleBuilder(currentBatchId);
          }
          // Refresh cards list
          await fetchCards(null, false);
        }}
      />
    </DashboardContainer>
  );
}

export default CardsView;
